/**
 * tenantApi.ts — Unified tenant-scoped API client.
 *
 * This module contains ALL tenant-scoped API methods used by the TenantPortal.
 * It consolidates what was previously split across api.ts and the tenantApi
 * object in adminApi.ts.
 *
 * Platform/super-admin methods remain in adminApi.ts (adminApi object).
 * Token helpers are re-exported from adminApi.ts for convenience.
 */
import { apiClient, unwrapResponse } from './apiClient';
import { authService } from './authService';
import {
  getAuthToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  type User,
  type UserDetail,
  type InviteUserRequest,
  type UpdateUserRequest,
  type Group,
  type Role,
  type Permission,
  type Integration,
  type CreateIntegrationRequest,
  type InboundIntegration,
  type CreateInboundIntegrationRequest,
  type UpdateInboundIntegrationRequest,
  type OutboundIntegration,
  type CreateOutboundIntegrationRequest,
  type UpdateOutboundIntegrationRequest,
  type OutboundIntegrationAction,
  type CreateOutboundIntegrationActionRequest,
  type UpdateOutboundIntegrationActionRequest,
  type ApiKeyInfo,
  type RoutingRule,
  type CreateRoutingRuleRequest,
  type UpdateRoutingRuleRequest,
  type Schedule,
  type ScheduleMember,
  type ScheduleOverride,
  type CreateScheduleRequest,
  type Rule,
  type CreateRuleRequest,
  type RuleAction,
  type Condition,
  type ConditionGroup,
  type ActionConfig,
  type CreateOverrideRequest,
  type NotificationChannel,
  type CreateNotificationChannelRequest,
  type UpdateNotificationChannelRequest,
  type UserNotificationPreference,
  type NotificationTarget,
  type SmsGateway,
  type CreateSmsGatewayRequest,
  type UpdateSmsGatewayRequest,
  type SmsDispatchMessage,
  type CreateSmsDispatchMessageRequest,
  type SmsGatewayOnboardingPayloadRequest,
  type SmsGatewayOnboardingPayloadResponse,
} from './adminApi';

export type {
  OutboundIntegrationAction,
  CreateOutboundIntegrationActionRequest,
  UpdateOutboundIntegrationActionRequest,
} from './adminApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const TENANT_RESTRICTED_ROLES = new Set(['platformadmin', 'portaladmin']);

/** Fetch JSON and unwrap IActionResult serialization wrapper. */
async function fetchJson<T>(response: Response): Promise<T> {
  const raw = await response.json();
  return unwrapResponse<T>(raw);
}

async function fetchIncidentJson<T>(response: Response): Promise<T> {
  const raw: unknown = await response.json();
  let payload = raw;
  let status: unknown;
  if (raw && typeof raw === 'object') {
    if ('Value' in raw && 'StatusCode' in raw) { payload = raw.Value; status = raw.StatusCode; }
    else if ('value' in raw && 'statusCode' in raw) { payload = raw.value; status = raw.statusCode; }
  }
  if (payload && typeof payload === 'object' && 'error' in payload && typeof payload.error === 'string')
    throw new Error(payload.error);
  if (typeof status === 'number' && status >= 400) throw new Error(`Incident request failed (${status}).`);
  return unwrapResponse<T>(payload);
}

async function buildApiError(response: Response, fallbackMessage: string): Promise<Error> {
  const statusSuffix = response.status ? ` (${response.status})` : '';

  try {
    const rawText = await response.text();
    if (!rawText) {
      return new Error(`${fallbackMessage}${statusSuffix}`);
    }

    try {
      const parsed = JSON.parse(rawText);
      const unwrapped = unwrapResponse<any>(parsed);
      const detail =
        (unwrapped && (unwrapped.error || unwrapped.message)) ||
        (parsed && (parsed.error || parsed.message));
      if (detail) {
        return new Error(`${fallbackMessage}${statusSuffix}: ${detail}`);
      }
    } catch {
      // Non-JSON response; fall through to raw text handling.
    }

    return new Error(`${fallbackMessage}${statusSuffix}: ${rawText}`);
  } catch {
    return new Error(`${fallbackMessage}${statusSuffix}`);
  }
}

/** Throws on HTTP errors and on IActionResult-wrapped errors (outer 200, inner 4xx/5xx). */
async function readApiResult<T>(response: Response, fallbackMessage: string): Promise<T> {
  if (!response.ok) throw await buildApiError(response, fallbackMessage);
  const rawText = await response.text();
  if (!rawText) return undefined as T;
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawText);
  } catch {
    throw new Error(`${fallbackMessage}: invalid JSON response`);
  }
  let status: unknown;
  if (parsed && typeof parsed === 'object') {
    if ('statusCode' in parsed) status = parsed.statusCode;
    else if ('StatusCode' in parsed) status = parsed.StatusCode;
  }
  if (typeof status === 'number' && status >= 400) {
    let payload: unknown = parsed;
    if (parsed && typeof parsed === 'object') {
      if ('value' in parsed) payload = parsed.value;
      else if ('Value' in parsed) payload = parsed.Value;
    }
    const detail = payload && typeof payload === 'object' && 'error' in payload && typeof payload.error === 'string'
      ? `: ${payload.error}` : '';
    throw new Error(`${fallbackMessage} (${status})${detail}`);
  }
  return unwrapResponse<T>(parsed);
}

function authHeader(): Record<string, string> {
  const token = getAuthToken() || localStorage.getItem('authToken') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function jsonHeaders(): Record<string, string> {
  return { ...authHeader(), 'Content-Type': 'application/json' };
}

function normalizeTenantSettings(raw: any): TenantSettings {
  const source = raw || {};
  return {
    tenantId: source.tenantId || source.TenantId || '',
    name: source.name || source.Name || '',
    slug: source.slug || source.Slug || '',
    region: source.region || source.Region || '',
    plan: source.plan || source.Plan || '',
    retentionDays: Number(source.retentionDays ?? source.RetentionDays ?? 30),
    authType: source.authType || source.AuthType || '',
    entraTenantId: source.entraTenantId || source.EntraTenantId || '',
    ssoAutoProvisionUsers: source.ssoAutoProvisionUsers ?? source.SsoAutoProvisionUsers ?? true,
    defaultNewUserRole: source.defaultNewUserRole || source.DefaultNewUserRole || '',
    defaultNewUserGroupId: source.defaultNewUserGroupId || source.DefaultNewUserGroupId || '',
    status: source.status || source.Status || '',
    subscriptionState: source.subscriptionState || source.SubscriptionState,
    evaluationExpiresAt: source.evaluationExpiresAt || source.EvaluationExpiresAt,
    planExpiry: source.planExpiry || source.PlanExpiry,
    planDaysLeft: source.planDaysLeft ?? source.PlanDaysLeft,
    subscriptionBillingPeriod: source.subscriptionBillingPeriod || source.SubscriptionBillingPeriod,
    subscriptionLastPaymentAt: source.subscriptionLastPaymentAt || source.SubscriptionLastPaymentAt,
    clientInitialSignupAt: source.clientInitialSignupAt || source.ClientInitialSignupAt,
    notificationPolicyType: source.notificationPolicyType || source.NotificationPolicyType,
    notificationTimeZone: source.notificationTimeZone || source.NotificationTimeZone,
    notificationWindow: source.notificationWindow || source.NotificationWindow,
    evaluationCardEnabled: source.evaluationCardEnabled ?? source.EvaluationCardEnabled,
    smsGatewayAlertMode: source.smsGatewayAlertMode || source.SmsGatewayAlertMode,
  };
}

function normalizeNotificationInboxItems(raw: any): NotificationInboxItem[] {
  if (Array.isArray(raw)) {
    return raw;
  }

  if (raw && typeof raw === 'object') {
    if (Array.isArray((raw as any).items)) {
      return (raw as any).items;
    }
    if (Array.isArray((raw as any).notifications)) {
      return (raw as any).notifications;
    }
  }

  return [];
}

function assertTenantSettingsPayload(raw: any): void {
  const source = raw || {};
  const explicitError = source.error || source.Error || source.message || source.Message;
  if (explicitError) {
    throw new Error(String(explicitError));
  }

  const hasIdentity = Boolean(
    source.tenantId || source.TenantId || source.slug || source.Slug || source.name || source.Name
  );
  if (!hasIdentity) {
    throw new Error('Failed to fetch tenant settings');
  }
}

// ─── Outbound credential types ──────────────────────────────────────────────

export interface OutboundCredentialBearer { authType: 'bearer'; token: string; }
export interface OutboundCredentialBasic { authType: 'basic'; username: string; password: string; }
export interface OutboundCredentialApiKey { authType: 'apikey'; headerName: string; headerValue: string; }
export type OutboundCredential =
  | OutboundCredentialBearer
  | OutboundCredentialBasic
  | OutboundCredentialApiKey;

// ─── Ingestion health types ─────────────────────────────────────────────────

export interface IngestionIntegrationHealth {
  integrationId: string;
  integrationName: string;
  failures1h: number;
  failures24h: number;
  successes1h: number;
  successes24h: number;
  lastSuccess: string | null;
  lastFailure: string | null;
  status: 'healthy' | 'degraded' | 'warning' | 'critical';
}

export interface IngestionHealthResponse {
  integrations: IngestionIntegrationHealth[];
  overallStatus: 'healthy' | 'degraded' | 'warning' | 'critical';
}

export interface IngestionFailure {
  attemptId: string;
  integrationId: string | null;
  failureReason: string;
  sourceIp: string;
  attemptedAt: string;
}

export interface UnmatchedInboundEvent {
  eventId: string;
  integrationId: string | null;
  triggerType: string;
  fingerprint: string;
  correlationId: string;
  externalAlertId: string;
  externalTicketId: string;
  reason: string;
  receivedAt: string;
  payloadJson: string;
}

// ─── Escalation policy types ────────────────────────────────────────────────

export interface EscalationStepDto {
  stepId: string;
  stepOrder: number;
  targetType: string;
  targetId: string;
  timeoutMinutes: number;
  resolutionMinutes: number | null;
  channels: string;
}

export interface EscalationPolicyDto {
  policyId: string;
  name: string;
  description: string;
  priority: string | null;
  repeatCount: number;
  fallbackTargetType: string | null;
  fallbackTargetId: string | null;
  fallbackEnabled: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  steps: EscalationStepDto[];
}

export interface EscalationStepRequest {
  stepOrder?: number;
  targetType: string;
  targetId: string;
  timeoutMinutes?: number;
  resolutionMinutes?: number;
  channels?: string;
}

export interface CreateEscalationPolicyRequest {
  name: string;
  description?: string;
  priority?: string | null;
  repeatCount?: number;
  fallbackTargetType?: string;
  fallbackTargetId?: string;
  fallbackEnabled?: boolean;
  isDefault?: boolean;
  steps: EscalationStepRequest[];
}

export interface UpdateEscalationPolicyRequest {
  name?: string;
  description?: string;
  priority?: string | null;
  repeatCount?: number;
  fallbackTargetType?: string;
  fallbackTargetId?: string;
  fallbackEnabled?: boolean;
  isDefault?: boolean;
  steps?: EscalationStepRequest[];
}

// ─── Escalation info type (on alerts) ───────────────────────────────────────

export interface EscalationInfoDto {
  policyId: string;
  policyName: string;
  currentTier: number;
  totalTiers: number;
  escalationMode: string;
  isComplete: boolean;
  tierStartedAt: string;
  timeoutMinutes: number;
  secondsRemaining: number;
  nextAction: string;
  fallbackEnabled: boolean;
}

// ─── Alert filter types ─────────────────────────────────────────────────────

export interface AlertFilters {
  status?: string;
  severity?: string;
  service?: string;
  search?: string;
  assignedTo?: string;
  startUtc?: string;
  endUtc?: string;
  limit?: number;
  skip?: number;
}

export interface AlertsResponse {
  alerts: any[];
  totalCount: number;
  skip: number;
  limit: number;
}

export type NotificationScope = 'mine' | 'team' | 'shared';

export interface NotificationInboxItem {
  notificationId: string;
  alertId: string | null;
  sourceType?: string;
  receiptId?: string;
  integrationId?: string;
  deepLink?: string;
  schemaState?: string;
  scope: NotificationScope;
  title: string;
  subtitle?: string;
  severity?: 'critical' | 'high' | 'medium' | 'low' | 'info';
  status?: string;
  service?: string;
  createdAt: string;
  updatedAt: string;
  isUnread: boolean;
}

export interface InboundNotificationPage {
  items: NotificationInboxItem[];
  limit: number;
  nextCursor: string | null;
  total: number;
  availableServices?: string[];
  availableStatuses?: string[];
}

export interface NotificationTimelineEvent {
  eventId: string;
  eventType: string;
  timestamp: string;
  workflowId?: string | null;
  workflowName?: string | null;
  instanceId?: string | null;
  stepId?: string | null;
  stepOrder?: number | null;
  status?: string | null;
  rawDetails: string;
  canReplay?: boolean;
}

export interface NotificationEmailHistoryItem {
  requestId: string;
  attemptId: string | null;
  attemptNumber: number | null;
  status: string;
  errorClass: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  unresolvedTokens: string[];
  httpStatusCode: number | null;
  providerCorrelationId: string | null;
  providerReceiptId: string | null;
  workflowInstanceId: string | null;
  workflowStepId: string | null;
  createdAt: string;
  completedAt: string | null;
  envelope: { provider: string; from: string | null; to: string[]; cc: string[]; bcc: string[]; transportInvoked: boolean; attachments?: EmailAttachmentSummary[] | null } | null;
}

export interface EmailAttachmentSummary {
  sourceField: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  status: 'Attached' | 'SkippedMissing' | string;
}

export interface NotificationEmailHistoryPage {
  items: NotificationEmailHistoryItem[];
  nextSkip: number | null;
}

export interface InboundNotificationDetail {
  notificationId: string;
  sourceType: string;
  title: string;
  description: string;
  status: string;
  severity: string;
  service: string;
  source: string;
  createdAt: string;
  updatedAt: string;
  rawPayload: string;
  normalisedPayload?: string | null;
  integrationId?: string | null;
  integrationName?: string | null;
  timeline: NotificationTimelineEvent[];
}

// ─── Workflow types ─────────────────────────────────────────────────────────

export interface WorkflowTriggerConditions {
  severity?: string[];
  service?: string[];
  source?: string[];
}

export interface WorkflowDto {
  workflowId: string;
  inboundIntegrationId?: string | null;
  globalVariables?: string[];
  name: string;
  description: string;
  triggerType: string;
  triggerConditions: WorkflowTriggerConditions | null;
  isEnabled: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type WorkflowSourceCompatibility = 'Alert' | 'Notification' | 'Either';

export interface SubWorkflowDto {
  workflowId: string;
  name: string;
  description: string;
  scope: 'Shared' | 'Private';
  ownerWorkflowId: string | null;
  clonedFromWorkflowId: string | null;
  sourceCompatibility: WorkflowSourceCompatibility;
  resultCodes: string[];
  globalVariables: string[];
  definitionRevision: number;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SubWorkflowUsageResponse {
  workflowId: string;
  callers: Array<{ workflowId: string; name: string; kind: string; ownerWorkflowId: string | null }>;
  runningInstances: Array<{
    instanceId: string;
    parentInstanceId: string | null;
    parentStepId: string | null;
    callDepth: number;
    status: string;
    resultCode: string | null;
    startedAt: string;
    completedAt: string | null;
  }>;
}

export interface SubWorkflowHistoryEntry extends WorkflowInstanceDto {
  parentInstanceId: string | null;
  parentStepId: string | null;
  subWorkflowSnapshotId: string | null;
  callDepth: number;
  resultCode: string | null;
  resultOutputsJson: string | null;
  snapshotRevision: number;
  snapshotJson: string;
  snapshotHash: string;
}

export interface CreateSubWorkflowRequest {
  name: string;
  description?: string;
  sourceCompatibility: WorkflowSourceCompatibility;
  resultCodes: string[];
  globalVariables?: string[];
  isEnabled?: boolean;
}

export interface CreatePrivateSubWorkflowRequest {
  name: string;
  description?: string;
  resultCodes: string[];
  globalVariables?: string[];
  isEnabled?: boolean;
}

export interface ConvertPrivateSubWorkflowRequest {
  name: string;
  expectedDefinitionRevision: number;
  expectedOwnerUpdatedAt: string;
}

export interface UpdateSubWorkflowRequest extends Partial<CreateSubWorkflowRequest> {
  expectedDefinitionRevision: number;
}

export interface WorkflowStepDto {
  stepId: string;
  workflowId: string;
  stepOrder: number;
  stepType: string;
  name: string;
  config: any;
  continueOnError: boolean;
}

export interface WorkflowInstanceDto {
  instanceId: string;
  workflowId: string;
  parentInstanceId?: string | null;
  parentStepId?: string | null;
  subWorkflowSnapshotId?: string | null;
  callDepth?: number;
  alertId: string | null;
  status: string;
  currentStepOrder: number;
  triggerType: string;
  errorMessage: string | null;
  resultCode?: string | null;
  resultOutputsJson?: string | null;
  startedAt: string;
  completedAt: string | null;
}

export interface WorkflowStateEntryDto {
  stateId: string;
  stepId: string;
  stepOrder: number;
  status: string;
  errorMessage: string | null;
  inputJson?: string | null;
  outputJson?: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

export interface MobileDeviceRecord {
  deviceId: string;
  installationId: string;
  platform: string;
  pushTokenMasked: string;
  appVersion?: string;
  supportsNativeIncomingPush: boolean;
  isOnline: boolean;
  isRevoked: boolean;
  reregisterRequired: boolean;
  userId?: string;
  lastSeenAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWorkflowRequest {
  inboundIntegrationId?: string | null;
  globalVariables?: string[];
  name: string;
  description?: string;
  triggerType: string;
  triggerConditions?: WorkflowTriggerConditions;
  isEnabled?: boolean;
}

export interface UpdateWorkflowRequest {
  inboundIntegrationId?: string | null;
  globalVariables?: string[];
  name?: string;
  description?: string;
  triggerType?: string;
  triggerConditions?: WorkflowTriggerConditions;
  isEnabled?: boolean;
}

export interface GroupMemberUser {
  userId: string;
  email: string;
  displayName: string;
  status: string;
  timezone?: string;
}

export interface GroupRoleAssignment {
  roleId: string;
  name: string;
  description?: string;
  assigned: boolean;
}

export interface GroupRolesResponse {
  group: {
    groupId: string;
    name: string;
    type: string;
  };
  roles: GroupRoleAssignment[];
}

export interface GroupSourceGroupsResponse {
  groupId: string;
  sourceGroupIds: string[];
}

export interface GroupTeamLeadsResponse {
  groupId: string;
  teamLeadUserIds: string[];
}

export interface RolePermissionsResponse {
  role: {
    roleId: string;
    name: string;
    description?: string;
  };
  permissions: Array<Permission & { assigned: boolean }>;
}

export interface WorkflowStepRequest {
  stepId?: string;             // Preserve existing StepId on update; omit for new steps
  stepOrder?: number;
  stepType: string;
  name?: string;
  config?: any;
  continueOnError?: boolean;
}

export interface WorkflowIntegrationActionConfig {
  integrationId: string;
  integrationVersion?: string;
  integrationActionId?: string;
  actionKey?: string;
  method?: string;
  pathTemplate?: string;
  bodyTemplate?: string;
  headers?: Record<string, string>;
  ticketIdVariable?: string;
  fieldMappings?: Record<string, string>;
}

export interface WebhookRelayResponse {
  success: boolean;
  statusCode: number;
  latencyMs: number;
  errorCode?: string;
  errorMessage?: string;
  responseBodyTruncated: boolean;
  responseBodyPreview?: string;
  mappingCandidates?: string[];
  responseHeaders?: Record<string, string>;
  request: {
    url: string;
    method: string;
    timeoutSeconds: number;
    retryCount: number;
  };
}

export interface WebhookRelayTestRequest {
  variables?: Record<string, string>;
  timeoutSeconds?: number;
}

export interface WorkflowWebhookTestRequest extends WebhookRelayTestRequest {
  config: {
    url: string;
    method?: string;
    headers?: Record<string, string>;
    bodyTemplate?: string;
    timeoutSeconds?: number;
    retryCount?: number;
    retryDelaySeconds?: number;
    integrationId?: string;
  };
}

export interface PromoteWorkflowWebhookStepRequest {
  integrationName: string;
  provider: string;
  actionName?: string;
  authType?: string;
  authSecretRef?: string;
  ticketIdVariable?: string;
  sortOrder?: number;
  fieldMappings?: Record<string, string>;
}

export interface PromoteWorkflowWebhookStepResponse {
  workflowId: string;
  stepId: string;
  integrationId: string;
  integrationVersion?: string;
  integrationName: string;
  actionId: string;
  actionName: string;
  promotedStepType: string;
}

export interface RebindWorkflowIntegrationStepRequest {
  integrationId: string;
  integrationActionId: string;
  integrationVersion?: string;
}

export interface RebindWorkflowIntegrationStepResponse {
  workflowId: string;
  stepId: string;
  integrationId: string;
  integrationVersion?: string;
  integrationName: string;
  actionId: string;
  actionName: string;
  reboundStepType: string;
}

export interface NotificationStepTarget {
  type: 'Channel' | 'User' | 'Group' | 'OnCall' | string;
  id: string;
}

export interface NotificationStepConfig {
  channelId?: string;
  templateOverride?: string;
  subjectTemplate?: string;
  bodyTemplate?: string;
  bodyFormat?: 'PlainText' | 'Html';
  bodySourceField?: string;
  targets?: NotificationStepTarget[];
  channelTypes?: string[];
  toEmails?: string;
  excludeChannelEmailRecipients?: boolean;
  ccEmails?: string;
  bccEmails?: string;
  adHocEmails?: string;
  attachments?: NotificationAttachmentMapping[];
  sendAttachmentsIfPresent?: boolean;
  doNotSendAttachments?: boolean;
}

export interface NotificationAttachmentMapping {
  sourceField: string;
  fileName?: string;
  contentType?: string;
}

export interface WorkflowNotificationContentValidationRequest {
  workflowId?: string;
  useLatestWorkflowInstance?: boolean;
  productionEmail?: boolean;
  alertId?: string;
  title?: string;
  description?: string;
  status?: string;
  severity?: string;
  service?: string;
  source?: string;
  magicLinkUrl?: string;
  config: NotificationStepConfig;
  variables?: Record<string, string>;
}

export interface WorkflowNotificationContentValidationResponse {
  isValid: boolean;
  error?: string | null;
  unresolvedTokens: string[];
  subjectPreview?: string | null;
  bodyPreview?: string | null;
  bodyHtmlPreview?: string | null;
  runtimeInstanceId?: string | null;
  runtimeVariablesUsed: boolean;
  attachmentPreview?: EmailAttachmentSummary[];
  attachmentError?: string | null;
}

export interface WorkflowConditionFieldMetadata {
  key: string;
  label: string;
  category: string;
}

export interface WorkflowConditionOperatorMetadata {
  key: string;
  label: string;
  hint: string;
}

export interface WorkflowConditionMetadata {
  fields: WorkflowConditionFieldMetadata[];
  operators: WorkflowConditionOperatorMetadata[];
  customFieldPrefix: string;
  notes: string;
}

export interface WorkflowSecret {
  name: string;
  token: string;
}

export interface TenantSettings {
  tenantId: string;
  name: string;
  slug: string;
  region: string;
  plan: string;
  retentionDays: number;
  authType: string;
  entraTenantId?: string;
  ssoAutoProvisionUsers?: boolean;
  defaultNewUserRole?: string;
  defaultNewUserGroupId?: string;
  status: string;
  subscriptionState?: string;
  evaluationExpiresAt?: string | null;
  planExpiry?: string | null;
  planDaysLeft?: number | null;
  subscriptionBillingPeriod?: string;
  subscriptionLastPaymentAt?: string | null;
  clientInitialSignupAt?: string | null;
  notificationPolicyType?: string;
  notificationTimeZone?: string;
  notificationWindow?: {
    daysOfWeek: number[];
    startTimeLocal: string;
    endTimeLocal: string;
  };
  evaluationCardEnabled?: boolean;
  smsGatewayAlertMode?: 'Disabled' | 'TenantAdminDashboard' | 'TenantAndSuperAdminDashboard' | string;
}

export interface TenantPlanOptionsResponse {
  plans: string[];
}

export interface TenantSubscriptionPlanFeature {
  featureId: string;
  text: string;
  icon?: string;
  sortOrder: number;
}

export interface TenantSubscriptionPlan {
  planId: string;
  levelLabel: string;
  planName: string;
  description?: string;
  monthlyPrice?: string;
  annualPrice?: string;
  ctaLabel: string;
  usersDisplay?: string;
  supportDisplay?: string;
  sortOrder: number;
  isFeatured: boolean;
  features: TenantSubscriptionPlanFeature[];
}

export interface TenantSubscriptionPlansResponse {
  currentPlan?: string;
  plans: TenantSubscriptionPlan[];
}

export interface TenantEntraConnectResponse {
  tenantId: string;
  entraTenantId?: string;
  authType: string;
  adminConsentUrl?: string;
  message?: string;
}

export interface ScimProvisioningConfig {
  tenantId: string;
  isEnabled: boolean;
  provisioningMode: 'Automatic' | 'Manual' | string;
  authorityTenantId?: string;
  scimBaseUrl?: string;
  clientId?: string;
  clientSecretRef?: string;
  isTestMode: boolean;
  lastSyncAt?: string | null;
  lastSyncStatus?: string;
  lastSyncMessage?: string;
  updatedAt?: string;
  createdAt?: string;
}

export interface ScimGroupIdentity {
  groupIdentityId: string;
  tenantId: string;
  localGroupId?: string;
  sourceProvider: string;
  sourceGroupId: string;
  sourceDisplayName?: string;
  isActive: boolean;
  updatedAt: string;
  createdAt: string;
}

export interface ScimGroupIdentityListResponse {
  tenantId: string;
  count: number;
  groups: ScimGroupIdentity[];
}

export interface UpsertScimGroupIdentityRequest {
  localGroupId: string;
  sourceProvider?: string;
  sourceGroupId: string;
  sourceDisplayName?: string;
  isActive?: boolean;
}

export interface ProvisionScimAppRequest {
  appDisplayName?: string;
  clientSecretRef?: string;
  secretLifetimeMonths?: number;
  enableScimAfterProvisioning?: boolean;
}

export interface ProvisionScimAppResponse {
  tenantId: string;
  authorityTenantId: string;
  clientId: string;
  clientSecretRef: string;
  appObjectId?: string;
  servicePrincipalObjectId?: string;
  isScimEnabled: boolean;
  provisioningMode: string;
  isTestMode: boolean;
  reusedExistingApp?: boolean;
  message: string;
}

export interface ScimProvisioningPreflightResponse {
  tenantId: string;
  authorityTenantId: string;
  entraConnected: boolean;
  bootstrapConfigured: boolean;
  graphReachable: boolean;
  canProvision: boolean;
  roles: string[];
  missingRoles: string[];
  approvalRequired: boolean;
  approvalUrl?: string;
  message: string;
}

export interface EntraGroupLookupItem {
  id: string;
  displayName: string;
}

export interface EntraGroupLookupResponse {
  tenantId: string;
  query: string;
  count: number;
  groups: EntraGroupLookupItem[];
}

export interface ScheduleWindow {
  windowId: string;
  scheduleId: string;
  name: string;
  startDayOfWeek: number;
  startTimeLocal: string;
  durationUnit: 'Hours' | 'Days' | string;
  durationValue: number;
  sequenceOrder: number;
  isActive: boolean;
}

export interface ScheduleRotationRule {
  ruleId: string;
  rotationId: string;
  dayOfWeek: number;
  startTimeLocal: string;
  endTimeLocal: string;
  isOvernight: boolean;
  durationMinutes?: number;
  sequenceOrder: number;
}

export interface ScheduleRosterEntry {
  rosterId: string;
  rotationId: string;
  userId: string;
  userName: string;
  userEmail: string;
  rotationOrder: number;
}

export interface ScheduleRotation {
  rotationId: string;
  windowId: string;
  name: string;
  groupId: string;
  assignmentDurationMinutes: number;
  priority: number;
  lockKey?: string;
  isActive: boolean;
}

export interface RotationSlotPreview {
  userId: string;
  startUtc: string;
  endUtc: string;
  slotIndex?: number;
}

export interface RosterRemovalRequest {
  userId: string;
  effectiveFromUtc?: string;
  reason?: string;
  preview?: boolean;
}

export interface RosterRemovalResult {
  status: string | number;
  scheduleId: string;
  rotationId: string;
  rotationName?: string;
  userId: string;
  preview: boolean;
  effectiveFromUtc: string;
  handoverUserId?: string | null;
  remainingMembers: number;
  before: RotationSlotPreview[];
  after: RotationSlotPreview[];
  gainedFirstSlotUtc?: Record<string, string>;
  cancelledSwapIds?: string[];
  revertedSwapIds?: string[];
}

export interface RotationConfig {
  rotation: ScheduleRotation;
  rules: ScheduleRotationRule[];
  roster: ScheduleRosterEntry[];
}

export interface ScheduleTimelineEntry {
  rotationId: string;
  rotationName: string;
  windowStartUtc: string;
  startUtc: string;
  endUtc: string;
  userId: string;
  userName: string;
  source: string;
  /** Cover/override id when the segment comes from a cover or team override. */
  sourceId?: string | null;
}

export type ScheduleCoverStatus = 'Active' | 'Cancelled' | 'Ended';

export interface ScheduleCover {
  coverId: string;
  scheduleId: string;
  rotationId?: string | null;
  coveredUserId: string;
  coveredUserName?: string | null;
  replacementUserId: string;
  replacementUserName?: string | null;
  startUtc: string;
  endUtc: string;
  reason?: string | null;
  status: ScheduleCoverStatus | string;
  createdBy?: string;
  createdAt?: string;
  cancelledBy?: string | null;
  cancelledAt?: string | null;
}

export interface ScheduleCoverBlock {
  rotationId?: string | null;
  rotationName?: string | null;
  startUtc: string;
  endUtc: string;
  inProgress: boolean;
}

export interface ScheduleCoverConflict {
  rotationId: string;
  rotationName?: string | null;
  startUtc: string;
  endUtc: string;
}

export interface ScheduleCoverRequest {
  coveredUserId: string;
  replacementUserId: string;
  rotationId?: string | null;
  startUtc?: string;
  endUtc?: string;
  reason: string;
  force?: boolean;
}

export interface ScheduleCoverCreateResult {
  cover: ScheduleCover;
  forced: boolean;
  coveredSegmentCount: number;
  affectedRotationIds: string[];
  replacementAlreadyOnCall: boolean;
  replacementConflicts: ScheduleCoverConflict[];
}

export type TeamOverrideStatus = 'Scheduled' | 'Active' | 'Ended' | 'Cancelled';
export type TeamOverrideTargetType = 'User' | 'Group';
export type TeamOverrideBucket = 'active' | 'scheduled' | 'past';

export interface TeamOverride {
  overrideId: string;
  name: string;
  reason?: string | null;
  targetType: TeamOverrideTargetType | string;
  targetId: string;
  targetName?: string | null;
  targetUserCount: number;
  startUtc: string;
  endUtc?: string | null;
  openEnded: boolean;
  status: TeamOverrideStatus | string;
  schedules: Array<{ scheduleId: string; name: string; scheduleType?: string | null }>;
  scheduleIds: string[];
  createdBy?: string;
  createdByName?: string | null;
  createdAt?: string;
  endedBy?: string | null;
  endedByName?: string | null;
  endedAt?: string | null;
  cancelledBy?: string | null;
  cancelledByName?: string | null;
  cancelledAt?: string | null;
}

export interface TeamOverrideRequest {
  name: string;
  reason?: string;
  targetType: TeamOverrideTargetType;
  targetId: string;
  scheduleIds: string[];
  mode: 'now' | 'window';
  startUtc?: string;
  endUtc?: string;
  confirmLargeGroup?: boolean;
}

export interface TeamOverrideConflict {
  overrideId: string;
  name: string;
  status: string;
  startUtc: string;
  endUtc?: string | null;
  scheduleIds: string[];
}

export interface TeamOverrideActiveSummaryItem {
  overrideId: string;
  name: string;
  targetType: string;
  targetId: string;
  targetName?: string | null;
  targetUserCount: number;
  startUtc: string;
  endUtc?: string | null;
  openEnded: boolean;
  status: string;
  scheduleIds: string[];
  scheduleNames: string[];
}

export interface TeamOverrideActiveSummary {
  count: number;
  canManage: boolean;
  overrides: TeamOverrideActiveSummaryItem[];
}

/** BL-043 BE-087/BE-088: mobile device presence of on-call operators. */
export type PresenceState = 'Live' | 'Stale' | 'None';

export interface PresenceDevice {
  platform?: string | null;
  appVersion?: string | null;
  lastSeenAtUtc?: string | null;
  pushPermission?: string | null;
  lastHeartbeatKind?: string | null;
  isLive: boolean;
  notLiveReason?: string | null;
}

export interface UserPresenceDto {
  userId: string;
  state: PresenceState | string;
  liveDeviceCount: number;
  lastSeenAtUtc?: string | null;
  staleAfterMinutes: number;
  devices: PresenceDevice[];
}

export interface OnCallPresenceUser {
  userId: string;
  userName?: string | null;
  source?: string | null;
  rotationName?: string | null;
  presence: UserPresenceDto;
}

export interface OnCallPresenceSlot {
  startUtc?: string | null;
  endUtc?: string | null;
  isOverride: boolean;
  users: OnCallPresenceUser[];
}

export interface OnCallPresenceSchedule {
  scheduleId: string;
  scheduleName: string;
  current?: OnCallPresenceSlot | null;
  next?: OnCallPresenceSlot | null;
}

export interface OnCallPresenceResponse {
  generatedAtUtc: string;
  staleAfterMinutes: number;
  schedules: OnCallPresenceSchedule[];
}

export type DevicePresenceSummary = Omit<UserPresenceDto, 'devices'> & { devices?: PresenceDevice[] };

export interface OperationalPresenceUser extends Omit<OnCallPresenceUser, 'presence'> {
  dutyRole?: string | null;
  presence: DevicePresenceSummary;
}

export interface OperationalPresenceSlot extends Omit<OnCallPresenceSlot, 'users'> {
  users: OperationalPresenceUser[];
}

export interface OperationalPresenceSchedule extends Omit<OnCallPresenceSchedule, 'current' | 'next'> {
  scheduleType: string;
  linkedPrimaryScheduleId?: string | null;
  current?: OperationalPresenceSlot | null;
  next?: OperationalPresenceSlot | null;
  leaders: OperationalPresenceUser[];
}

export interface OperationalPresenceResponse extends Omit<OnCallPresenceResponse, 'schedules'> {
  schedules: OperationalPresenceSchedule[];
}

export type PresenceIncidentStatus = 'Open' | 'Resolved' | 'Expired';

export interface PresenceIncidentDto {
  incidentId: string;
  scheduleId: string;
  scheduleName?: string | null;
  userId: string;
  userName?: string | null;
  shiftStartUtc: string;
  shiftEndUtc: string;
  detectedAtUtc: string;
  status: PresenceIncidentStatus | string;
  userNotifiedAtUtc?: string | null;
  userNoticeCount: number;
  escalatedAtUtc?: string | null;
  escalatedToUserIds: string[];
  resolvedAtUtc?: string | null;
  resolutionReason?: string | null;
  lastCheckedAtUtc?: string | null;
}

export interface PresenceSettings {
  enabled: boolean;
  globallyEnabled: boolean;
  graceMinutes: number;
  staleAfterMinutes: number;
  escalateAfterMinutes: number;
  recheckMinutes: number;
  maxUserReminders: number;
  groupOverrideAnyLive: boolean;
}

export interface MandatoryShiftReminder {
  enabled: true;
  mandatory: true;
}

/** BL-043 BE-089: 7d/24h/8h are opt-in; 1h and shift start are mandatory. */
export interface ShiftReminderPreferences {
  remind7d: boolean;
  remind24h: boolean;
  remind8h: boolean;
  oneHour: MandatoryShiftReminder;
  start: MandatoryShiftReminder;
  remindersEnabled: boolean;
  updatedAt?: string | null;
}

export type ShiftReminderPreferencesUpdate = Partial<Pick<ShiftReminderPreferences, 'remind7d' | 'remind24h' | 'remind8h'>>;

/** Maximum schedules per presence request (matches PresenceApi.MaxSchedulesPerRequest). */
export const PRESENCE_SCHEDULE_BATCH = 50;

/** Error from the on-call override API, carrying the structured body (conflicts, invalid schedules, group size). */
export class TeamOverrideApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly conflicts: TeamOverrideConflict[];
  readonly invalidScheduleIds: string[];
  readonly groupMemberCount?: number | null;

  constructor(status: number, message: string, body: any = {}) {
    super(message);
    this.name = 'TeamOverrideApiError';
    this.status = status;
    this.code = body?.code || undefined;
    this.conflicts = Array.isArray(body?.conflicts) ? body.conflicts : [];
    this.invalidScheduleIds = Array.isArray(body?.invalidScheduleIds) ? body.invalidScheduleIds : [];
    this.groupMemberCount = body?.groupMemberCount ?? null;
  }
}

async function readTeamOverrideResult<T>(response: Response, fallbackMessage: string): Promise<T> {
  const rawText = await response.text().catch(() => '');
  let parsed: any;
  try {
    parsed = rawText ? JSON.parse(rawText) : undefined;
  } catch {
    parsed = undefined;
  }
  if (!response.ok) {
    const body = parsed?.value ?? parsed?.Value ?? parsed ?? {};
    throw new TeamOverrideApiError(response.status, body?.error || `${fallbackMessage} (${response.status})`, body);
  }
  if (parsed === undefined) return undefined as T;
  try {
    return unwrapResponse<T>(parsed);
  } catch (err) {
    // IActionResult-wrapped error (outer 200, inner 4xx): keep the structured body.
    const details = (err as { details?: any; status?: number })?.details;
    throw new TeamOverrideApiError((err as { status?: number })?.status ?? 500,
      details?.error || (err as Error)?.message || fallbackMessage, details || {});
  }
}

export interface ScheduleGap {
  windowId: string;
  startUtc: string;
  endUtc: string;
  reason: string;
}

export interface ScheduleShiftSwap {
  swapId: string;
  scheduleId: string;
  rotationId?: string;
  userAId: string;
  userBId: string;
  startUtc: string;
  endUtc: string;
  status: string;
  reason?: string;
  requestedAt: string;
  canApprove?: boolean;
  canReject?: boolean;
}

export interface RosterAllocationPreviewItem {
  index: number;
  startUtc: string;
  endUtc: string;
  userId: string;
  userName: string;
  rotationId: string;
  rotationName: string;
  lockSourceRotationId?: string;
}

export interface ExchangeMailboxRegistration {
  mailboxRegistrationId: string;
  mailboxObjectId: string;
  name: string;
  isEnabled: boolean;
  isRetired: boolean;
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export interface ExchangeSenderRegistration {
  senderIdentityId: string;
  mailboxRegistrationId: string;
  mailboxName: string;
  mailboxRevision: number;
  label: string;
  fromAddress: string;
  displayName?: string | null;
  isEnabled: boolean;
  isRetired: boolean;
  revision: number;
  evidenceRevision?: number | null;
  verifiedAtUtc?: string | null;
  controlledSendAccepted?: boolean;
  receivedAliasConfirmed?: boolean;
  canStartControlledTest?: boolean;
  testDeliveryMode?: 'LocalCapture' | 'External';
  testUnavailableReason?: string | null;
  canSend: boolean;
  eligibilityCode: string;
}

export interface NotificationDeliveryFailure {
  requestId: string;
  status: string;
  dispatchReason?: string | null;
  dispatchSource?: string | null;
  createdAt: string;
  updatedAt: string;
  attemptCount: number;
  lastChannelType?: string | null;
  lastDeliveryProvider?: string | null;
  lastAttemptStatus?: string | null;
  lastErrorClass?: string | null;
  lastErrorCode?: string | null;
  lastAttemptAt?: string | null;
  lastProviderCorrelationId?: string | null;
  lastMailboxName?: string | null;
  latestReplayCommandId?: string | null;
  latestReplayState?: string | null;
}

export interface NotificationDeliveryFailureFilters {
  status?: string;
  channelType?: string;
  deliveryProvider?: string;
  mailboxName?: string;
  updatedAfterUtc?: string;
  updatedBeforeUtc?: string;
}

export interface NotificationDeliveryAttempt {
  attemptId: string;
  requestId: string;
  channelType: string;
  attemptNumber: number;
  deliveryProvider?: string | null;
  providerCorrelationId?: string | null;
  status: string;
  errorClass?: string | null;
  errorCode?: string | null;
  createdAt: string;
  completedAt?: string | null;
}

export interface NotificationReplayOperation {
  replayCommandId: string;
  requestId: string;
  state: string;
  publishAttemptCount: number;
  nextAttemptUtc?: string | null;
  lastErrorCode?: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string | null;
  cancelledById?: string | null;
  cancelledAt?: string | null;
  cancellationReason?: string | null;
  snapshotExpiresAt?: string | null;
  snapshotAvailable?: boolean;
}

export interface BulkNotificationReplayItemResult {
  requestId: string;
  outcome: string;
  replayCommandId?: string | null;
  state?: string | null;
}

export interface BulkNotificationReplayResponse {
  operationId: string;
  state: string;
  items: BulkNotificationReplayItemResult[];
}

export interface BulkNotificationReplaySummary {
  operationId: string;
  state: string;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ExchangeSenderTestOperation {
  operationId: string;
  senderIdentityId: string;
  state: string;
  publishAttemptCount: number;
  nextAttemptUtc?: string | null;
  providerStatusCode?: number | null;
  providerCorrelationId?: string | null;
  errorCode?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
}

export interface NotificationDeliveryIncident {
  incidentId: string;
  tenantId?: string | null;
  affectedGroupId?: string | null;
  provider: string;
  channelType: string;
  failureClass: string;
  errorCode?: string | null;
  severity: string;
  state: string;
  occurrenceCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  lastRecoveredAt?: string | null;
  isAcknowledged: boolean;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  resolutionReason?: string | null;
  archivedAt?: string | null;
  archivedBy?: string | null;
  latestRetest?: {
    requestId: string;
    recipientEmail: string;
    requestedBy: string;
    requestedAt: string;
    status: 'Queued' | 'InProgress' | 'Succeeded' | 'Failed';
    errorCode?: string | null;
    completedAt?: string | null;
  } | null;
}

export interface NotificationPlatformIncidentSummary {
  activeCriticalCount: number;
  oldestActiveAt?: string | null;
  mostRecentOccurrenceAt?: string | null;
}

// ─── Unified tenant API ─────────────────────────────────────────────────────

export const tenantApi = {
  // ── Alerts ──────────────────────────────────────────────────────────────

  async getAlerts(filters?: AlertFilters): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.severity) params.append('severity', filters.severity);
    if (filters?.service) params.append('service', filters.service);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.assignedTo) params.append('assignedTo', filters.assignedTo);
    if (filters?.startUtc) params.append('startUtc', filters.startUtc);
    if (filters?.endUtc) params.append('endUtc', filters.endUtc);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.skip) params.append('skip', filters.skip.toString());

    const response = await fetch(`${API_BASE_URL}/api/alerts?${params.toString()}`, {
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch alerts');
    const raw = await response.json();
    const data = unwrapResponse<any>(raw);
    const alerts = data.alerts || [];
    // Ensure id property for components expecting it
    return alerts.map((a: any) => ({ ...a, id: a.alertId }));
  },

  async getAlert(alertId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/${alertId}`, {
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch alert');
    const raw = await response.json();
    const alert = unwrapResponse<any>(raw);
    if (alert) alert.id = alert.alertId;
    return alert;
  },

  async acknowledgeAlert(alertId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/${alertId}/ack`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to acknowledge alert');
  },

  async closeAlert(alertId: string, resolutionNote?: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/${alertId}/close`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ resolutionNote }),
    });
    if (!response.ok) throw new Error('Failed to close alert');
  },

  async reopenAlert(alertId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/${alertId}/reopen`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to reopen alert');
  },

  async addNote(alertId: string, content: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/${alertId}/notes`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ content }),
    });
    if (!response.ok) throw new Error('Failed to add note');
  },

  async assignAlert(alertId: string, userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/${alertId}/assign`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ assignToUserId: userId }),
    });
    if (!response.ok) throw new Error('Failed to assign alert');
  },

  async getAlertsWithMeta(filters?: AlertFilters): Promise<AlertsResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.severity) params.append('severity', filters.severity);
    if (filters?.service) params.append('service', filters.service);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.assignedTo) params.append('assignedTo', filters.assignedTo);
    if (filters?.startUtc) params.append('startUtc', filters.startUtc);
    if (filters?.endUtc) params.append('endUtc', filters.endUtc);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.skip != null) params.append('skip', filters.skip.toString());

    const response = await fetch(`${API_BASE_URL}/api/alerts?${params.toString()}`, {
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch alerts');
    const raw = await response.json();
    const data = unwrapResponse<any>(raw);
    const alerts = (data.alerts || []).map((a: any) => ({ ...a, id: a.alertId }));
    return {
      alerts,
      totalCount: data.totalCount ?? alerts.length,
      skip: data.skip ?? 0,
      limit: data.limit ?? 50,
    };
  },

  async getAlertServices(): Promise<string[]> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/meta/services`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch alert services');
    const raw = await response.json();
    const data = unwrapResponse<{ services: string[] }>(raw);
    return data.services || [];
  },

  async listMobileDevices(): Promise<MobileDeviceRecord[]> {
    const response = await fetch(`${API_BASE_URL}/api/mobile/devices`, {
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch mobile devices');
    const raw = await response.json();
    const data = unwrapResponse<any>(raw);
    return Array.isArray(data?.devices) ? data.devices : [];
  },

  async revokeMobileDevice(installationId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/mobile/devices/${encodeURIComponent(installationId)}/revoke`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to revoke mobile device');
  },

  async requestMobileDeviceReregister(installationId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/mobile/devices/${encodeURIComponent(installationId)}/reregister`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to request mobile re-register');
  },

  async removeMobileDevice(installationId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/mobile/devices/${encodeURIComponent(installationId)}`, {
      method: 'DELETE',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to remove mobile device');
  },

  async changePriority(alertId: string, priority: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/${alertId}/priority`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ priority }),
    });
    if (!response.ok) throw new Error('Failed to change priority');
  },

  // ── Users ───────────────────────────────────────────────────────────────

  async getUsers(params?: { skip?: number; take?: number; search?: string }): Promise<{ users: User[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.skip) searchParams.append('skip', params.skip.toString());
    if (params?.take) searchParams.append('take', (params.take || 50).toString());
    if (params?.search) searchParams.append('search', params.search);

    const response = await fetch(`${API_BASE_URL}/api/users?${searchParams}`, {
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch users');
    return fetchJson(response);
  },

  async getUser(userId: string): Promise<UserDetail> {
    const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch user');
    return fetchJson(response);
  },

  async inviteUser(data: InviteUserRequest): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/api/users/invite`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const text = await response.text();
      let errorMsg = 'Failed to invite user';
      try {
        const parsed = JSON.parse(text);
        const unwrapped = unwrapResponse<any>(parsed);
        errorMsg = unwrapped.error || errorMsg;
      } catch { /* use default */ }
      throw new Error(errorMsg);
    }
    return fetchJson(response);
  },

  async updateUser(userId: string, data: Partial<UpdateUserRequest>): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update user');
    return fetchJson(response);
  },

  async deleteUser(userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });

    const raw = await response.json().catch(() => null);

    if (!response.ok) {
      let errorMsg = 'Failed to delete user';
      if (raw) {
        try {
          const data = unwrapResponse<any>(raw);
          errorMsg = data?.error || data?.message || errorMsg;
        } catch (err: any) {
          errorMsg = err?.message || errorMsg;
        }
      }
      throw new Error(errorMsg);
    }

    if (raw) {
      unwrapResponse(raw);
    }
  },

  async assignRoles(userId: string, roles: string[]): Promise<{ roles: string[] }> {
    const response = await fetch(`${API_BASE_URL}/api/users/${userId}/roles`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ roles }),
    });
    if (!response.ok) {
      throw await buildApiError(response, 'Failed to assign roles');
    }
    return fetchJson(response);
  },

  async resendInvitation(userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/users/${userId}/resend-invitation`, {
      method: 'POST',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to resend invitation');
  },

  async updateProfile(data: {
    displayName?: string;
    phone?: string;
    timezone?: string;
    dateTimeLocale?: string | null;
    hourCycle?: import('../shared/dateTimePreferences').HourCycle | null;
    notificationPreferences?: {
      email?: boolean;
      sms?: boolean;
      push?: boolean;
      teams?: boolean;
      quietHoursEnabled?: boolean;
      quietHoursStart?: string;
      quietHoursEnd?: string;
      timezone?: string;
    };
  }): Promise<void> {
    const sessionRevision = authService.getSessionRevision();
    const response = await fetch(`${API_BASE_URL}/api/users/profile`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update profile');
    if (sessionRevision !== authService.getSessionRevision()) throw new Error('Session changed while saving profile');
    const user = await this.getCurrentUser();
    if (sessionRevision !== authService.getSessionRevision()) throw new Error('Session changed while saving profile');
    authService.adoptUser(user, sessionRevision);
  },

  async getCurrentUser(): Promise<any> {
    const sessionRevision = authService.getSessionRevision();
    const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Not authenticated');
    const data = unwrapResponse<any>(await response.json());
    const rawUser = data?.user || data;
    if (!rawUser) return rawUser;

    rawUser.id = rawUser.id || rawUser.userId || rawUser.UserId;
    rawUser.userId = rawUser.userId || rawUser.UserId || rawUser.id;
    rawUser.tenantId = rawUser.tenantId || rawUser.TenantId || rawUser.tenant_id;
    rawUser.tenantName = rawUser.tenantName || rawUser.TenantName || rawUser.tenant_name;

    authService.adoptUser(rawUser, sessionRevision);
    return rawUser;
  },

  async getTenantSettings(): Promise<TenantSettings> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/settings`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch tenant settings');
    const data = await fetchJson<any>(response);
    assertTenantSettingsPayload(data);
    return normalizeTenantSettings(data);
  },

  async getTenantPlanOptions(): Promise<string[]> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/plan-options`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch tenant plan options');
    const data = await fetchJson<TenantPlanOptionsResponse>(response);
    return Array.isArray(data?.plans) ? data.plans : [];
  },

  async getTenantSubscriptionPlans(): Promise<TenantSubscriptionPlansResponse> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/plans`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch tenant subscription plans');
    const data = await fetchJson<TenantSubscriptionPlansResponse>(response);
    return {
      currentPlan: data?.currentPlan,
      plans: Array.isArray(data?.plans) ? data.plans : [],
    };
  },

  async connectTenantEntraIdentityProvider(data: { entraTenantId: string; authType: 'Dual' | 'SSOPreferred' | 'SSORequired' }): Promise<TenantEntraConnectResponse> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/identity-providers/entra/connect`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const msg = body?.error || body?.message || 'Failed to connect Entra identity provider';
      throw new Error(msg);
    }
    return fetchJson<TenantEntraConnectResponse>(response);
  },

  async disconnectTenantEntraIdentityProvider(data: { confirmationText: 'I AGREE' }): Promise<TenantEntraConnectResponse> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/identity-providers/entra/disconnect`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const msg = body?.error || body?.message || 'Failed to disconnect Entra identity provider';
      throw new Error(msg);
    }
    return fetchJson<TenantEntraConnectResponse>(response);
  },

  async getTenantScimConfig(): Promise<ScimProvisioningConfig> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/scim/config`, {
      headers: authHeader(),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const msg = body?.error || body?.message || 'Failed to fetch SCIM configuration';
      throw new Error(msg);
    }
    return fetchJson<ScimProvisioningConfig>(response);
  },

  async updateTenantScimConfig(data: {
    isEnabled: boolean;
    provisioningMode?: 'Automatic' | 'Manual' | string;
    authorityTenantId?: string;
    scimBaseUrl?: string;
    clientId?: string;
    clientSecretRef?: string;
    isTestMode: boolean;
  }): Promise<ScimProvisioningConfig> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/scim/config`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const details = Array.isArray(body?.details) ? `: ${body.details.join(' ')}` : '';
      const msg = body?.error || body?.message || 'Failed to update SCIM configuration';
      throw new Error(`${msg}${details}`);
    }
    return fetchJson<ScimProvisioningConfig>(response);
  },

  async getTenantScimGroupIdentities(take: number = 100): Promise<ScimGroupIdentityListResponse> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/scim/groups?take=${encodeURIComponent(String(take))}`, {
      headers: authHeader(),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const msg = body?.error || body?.message || 'Failed to fetch SCIM groups';
      throw new Error(msg);
    }
    return fetchJson<ScimGroupIdentityListResponse>(response);
  },

  async upsertTenantScimGroupIdentity(data: UpsertScimGroupIdentityRequest): Promise<ScimGroupIdentity> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/scim/groups/upsert`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({
        localGroupId: data.localGroupId,
        sourceProvider: data.sourceProvider || 'Entra',
        sourceGroupId: data.sourceGroupId,
        sourceDisplayName: data.sourceDisplayName,
        isActive: data.isActive ?? true,
      }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const msg = body?.error || body?.message || 'Failed to upsert SCIM group mapping';
      throw new Error(msg);
    }
    return fetchJson<ScimGroupIdentity>(response);
  },

  async searchTenantScimEntraGroups(query: string, take: number = 20): Promise<EntraGroupLookupResponse> {
    const trimmed = (query || '').trim();
    if (trimmed.length < 2) {
      return { tenantId: '', query: trimmed, count: 0, groups: [] };
    }

    const qs = new URLSearchParams({
      q: trimmed,
      take: String(Math.max(1, Math.min(50, take || 20))),
    });

    const response = await fetch(`${API_BASE_URL}/api/tenant/scim/entra-groups/search?${qs.toString()}`, {
      headers: authHeader(),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const msg = body?.error || body?.message || 'Failed to search Entra groups';
      const details = body?.details ? `: ${body.details}` : '';
      const approvalUrl = typeof body?.approvalUrl === 'string' ? body.approvalUrl : '';
      const missingScopes = Array.isArray(body?.missingScopes)
        ? body.missingScopes.filter((scope: unknown) => typeof scope === 'string') as string[]
        : [];
      const approvalSegment = approvalUrl ? ' Re-consent is required for Entra group search.' : '';
      const scopeSegment = missingScopes.length > 0 ? ` Missing scopes: ${missingScopes.join(', ')}.` : '';
      const error = new Error(`${msg}${details}${scopeSegment}${approvalSegment}`.trim()) as Error & {
        status?: number;
        approvalUrl?: string;
        missingScopes?: string[];
      };
      error.status = response.status;
      error.approvalUrl = approvalUrl;
      error.missingScopes = missingScopes;
      throw error;
    }

    let payload: any;
    try {
      payload = await fetchJson<any>(response);
    } catch (err: any) {
      const approvalUrl = typeof err?.approvalUrl === 'string'
        ? err.approvalUrl
        : (typeof err?.details?.approvalUrl === 'string' ? err.details.approvalUrl : '');
      const missingScopes = Array.isArray(err?.missingScopes)
        ? err.missingScopes.filter((scope: unknown) => typeof scope === 'string') as string[]
        : (Array.isArray(err?.details?.missingScopes)
          ? err.details.missingScopes.filter((scope: unknown) => typeof scope === 'string') as string[]
          : []);
      const baseMessage = err?.message || 'Failed to search Entra groups';
      const scopeSegment = missingScopes.length > 0 ? ` Missing scopes: ${missingScopes.join(', ')}.` : '';
      const approvalSegment = approvalUrl ? ' Re-consent is required for Entra group search.' : '';
      const normalized = new Error(`${baseMessage}${scopeSegment}${approvalSegment}`.trim()) as Error & {
        status?: number;
        approvalUrl?: string;
        missingScopes?: string[];
      };
      normalized.status = Number(err?.status || 0) || undefined;
      normalized.approvalUrl = approvalUrl;
      normalized.missingScopes = missingScopes;
      throw normalized;
    }
    if (payload?.error || payload?.message) {
      const details = payload?.details ? `: ${payload.details}` : '';
      throw new Error(`${payload.error || payload.message}${details}`);
    }

    return {
      tenantId: payload?.tenantId || '',
      query: payload?.query || trimmed,
      count: Number(payload?.count || 0),
      groups: Array.isArray(payload?.groups) ? payload.groups : [],
    };
  },

  async provisionTenantScimApp(data: ProvisionScimAppRequest): Promise<ProvisionScimAppResponse> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/scim/provision-app`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data || {}),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const msg = body?.error || body?.message || 'Failed to provision SCIM application';
      const details = body?.details ? `: ${body.details}` : '';
      const missingRoles = Array.isArray(body?.missingRoles)
        ? body.missingRoles.filter((role: unknown) => typeof role === 'string') as string[]
        : [];
      const approvalUrl = typeof body?.approvalUrl === 'string' ? body.approvalUrl : '';
      const rolesSegment = missingRoles.length > 0 ? ` Missing roles: ${missingRoles.join(', ')}.` : '';
      const approvalSegment = approvalUrl ? ` Approval URL: ${approvalUrl}` : '';
      const error = new Error(`${msg}${details}${rolesSegment}${approvalSegment}`.trim()) as Error & {
        status?: number;
        missingRoles?: string[];
        approvalUrl?: string;
      };
      error.status = response.status;
      error.missingRoles = missingRoles;
      error.approvalUrl = approvalUrl;
      throw error;
    }
    return fetchJson<ProvisionScimAppResponse>(response);
  },

  async preflightTenantScimProvisioning(): Promise<ScimProvisioningPreflightResponse> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/scim/provision-app/preflight`, {
      method: 'GET',
      headers: authHeader(),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const msg = body?.error || body?.message || 'Failed to run SCIM provisioning preflight';
      throw new Error(msg);
    }
    return fetchJson<ScimProvisioningPreflightResponse>(response);
  },

  async updateTenantSettings(data: {
    name?: string;
    region?: string;
    plan?: string;
    authType?: string;
    entraTenantId?: string;
    ssoAutoProvisionUsers?: boolean;
    defaultNewUserRole?: string;
    defaultNewUserGroupId?: string;
    retentionDays?: number;
    smsGatewayAlertMode?: string;
    subscriptionState?: string;
    evaluationDays?: number;
    subscriptionBillingPeriod?: string;
    subscriptionLastPaymentAt?: string;
    notificationPolicyType?: string;
    notificationTimeZone?: string;
    notificationWindow?: {
      daysOfWeek: number[];
      startTimeLocal: string;
      endTimeLocal: string;
    };
  }): Promise<TenantSettings> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/settings`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const msg = body?.error || body?.message || 'Failed to update tenant settings';
      throw new Error(msg);
    }
    const updated = await fetchJson<any>(response);
    assertTenantSettingsPayload(updated);
    return normalizeTenantSettings(updated);
  },

  async logout(): Promise<void> {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => {});
    }
    clearTokens();
  },

  // ── Groups ──────────────────────────────────────────────────────────────

  async getGroups(): Promise<Group[]> {
    const response = await fetch(`${API_BASE_URL}/api/groups`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch groups');
    return fetchJson(response);
  },

  async createGroup(data: { name: string; type: string }): Promise<Group> {
    const response = await fetch(`${API_BASE_URL}/api/groups`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    return readApiResult<Group>(response, 'Failed to create group');
  },

  async updateGroup(groupId: string, data: { name: string; type: string }): Promise<Group> {
    const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    return readApiResult<Group>(response, 'Failed to update group');
  },

  async getGroupMembers(groupId: string): Promise<GroupMemberUser[]> {
    const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/members`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch group members');
    return fetchJson(response);
  },

  async addGroupMember(groupId: string, userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/members`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ userId }),
    });
    await readApiResult<unknown>(response, 'Failed to add group member');
  },

  async removeGroupMember(groupId: string, userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/members/${userId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    await readApiResult<unknown>(response, 'Failed to remove group member');
  },

  async deleteGroup(groupId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    await readApiResult<unknown>(response, 'Failed to delete group');
  },

  async getGroupSourceGroups(groupId: string): Promise<GroupSourceGroupsResponse> {
    const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/source-groups`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch group source groups');
    return fetchJson(response);
  },

  async updateGroupSourceGroups(groupId: string, sourceGroupIds: string[]): Promise<GroupSourceGroupsResponse> {
    const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/source-groups`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify({ sourceGroupIds }),
    });
    if (!response.ok) throw new Error('Failed to update group source groups');
    return fetchJson(response);
  },

  async getGroupTeamLeads(groupId: string): Promise<GroupTeamLeadsResponse> {
    const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/team-leads`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch group team leads');
    return fetchJson(response);
  },

  async updateGroupTeamLeads(groupId: string, teamLeadUserIds: string[]): Promise<GroupTeamLeadsResponse> {
    const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/team-leads`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify({ teamLeadUserIds }),
    });
    return readApiResult<GroupTeamLeadsResponse>(response, 'Failed to update group team leads');
  },

  // ── Roles ───────────────────────────────────────────────────────────────

  async getRoles(): Promise<Role[]> {
    const response = await fetch(`${API_BASE_URL}/api/roles`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch roles');
    const roles = await fetchJson<Role[]>(response);
    return (roles || []).filter((role) => !TENANT_RESTRICTED_ROLES.has((role.name || '').toLowerCase()));
  },

  async createRole(data: { name: string; description?: string }): Promise<Role> {
    const response = await fetch(`${API_BASE_URL}/api/roles`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create role');
    return fetchJson(response);
  },

  async updateRole(roleId: string, data: { name?: string; description?: string }): Promise<Role> {
    const response = await fetch(`${API_BASE_URL}/api/roles/${roleId}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update role');
    return fetchJson(response);
  },

  async deleteRole(roleId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/roles/${roleId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to delete role');
  },

  async getRolePermissions(roleId: string): Promise<RolePermissionsResponse> {
    const response = await fetch(`${API_BASE_URL}/api/roles/${roleId}/permissions`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to load role permissions');
    return fetchJson(response);
  },

  async updateRolePermissions(roleId: string, permissionIds: number[]): Promise<{ roleId: string; permissionIds: number[] }> {
    const response = await fetch(`${API_BASE_URL}/api/roles/${roleId}/permissions`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify({ permissionIds }),
    });
    if (!response.ok) throw new Error('Failed to update role permissions');
    return fetchJson(response);
  },

  async getGroupRoles(groupId: string): Promise<GroupRolesResponse> {
    const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/roles`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to load group role assignments');
    return fetchJson(response);
  },

  async updateGroupRoles(groupId: string, roleIds: string[]): Promise<{ groupId: string; roleIds: string[] }> {
    const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/roles`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify({ roleIds }),
    });
    if (!response.ok) throw new Error('Failed to update group role assignments');
    return fetchJson(response);
  },

  // ── Integrations ────────────────────────────────────────────────────────

  async getIntegrations(): Promise<Integration[]> {
    const response = await fetch(`${API_BASE_URL}/api/integrations`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch integrations');
    return fetchJson(response);
  },

  async createIntegration(data: CreateIntegrationRequest): Promise<Integration> {
    const response = await fetch(`${API_BASE_URL}/api/integrations`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create integration');
    return fetchJson(response);
  },

  async updateIntegration(integrationId: string, data: Partial<CreateIntegrationRequest>): Promise<Integration> {
    const response = await fetch(`${API_BASE_URL}/api/integrations/${integrationId}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update integration');
    return fetchJson(response);
  },

  async deleteIntegration(integrationId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/integrations/${integrationId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to delete integration');
  },

  async createIntegrationKey(integrationId: string): Promise<{ key: string; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/integrations/${integrationId}/keys`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to create API key');
    return fetchJson(response);
  },

  async getIntegrationKeys(integrationId: string): Promise<ApiKeyInfo[]> {
    const response = await fetch(`${API_BASE_URL}/api/integrations/${integrationId}/keys`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch API keys');
    return fetchJson(response);
  },

  async deleteIntegrationKey(integrationId: string, keyId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/integrations/${integrationId}/keys/${keyId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to deactivate API key');
  },

  async restoreIntegrationKey(integrationId: string, keyId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/integrations/${integrationId}/keys/${keyId}/restore`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to restore API key');
  },

  // ── Inbound Integrations ───────────────────────────────────────────────

  async getInboundIntegrations(): Promise<InboundIntegration[]> {
    const response = await fetch(`${API_BASE_URL}/api/inbound-integrations`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch inbound integrations');
    return fetchJson(response);
  },

  async getDeletedInboundIntegrations(): Promise<InboundIntegration[]> {
    const response = await fetch(`${API_BASE_URL}/api/inbound-integrations/deleted`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch archived inbound integrations');
    return fetchJson(response);
  },

  async restoreInboundIntegration(id: string): Promise<InboundIntegration> {
    const response = await fetch(`${API_BASE_URL}/api/inbound-integrations/${id}/restore`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || 'Failed to restore inbound integration');
    }
    return fetchJson(response);
  },

  async createInboundIntegration(data: CreateInboundIntegrationRequest): Promise<InboundIntegration> {
    const response = await fetch(`${API_BASE_URL}/api/inbound-integrations`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create inbound integration');
    return fetchJson(response);
  },

  async updateInboundIntegration(id: string, data: UpdateInboundIntegrationRequest): Promise<InboundIntegration> {
    const response = await fetch(`${API_BASE_URL}/api/inbound-integrations/${id}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update inbound integration');
    return fetchJson(response);
  },

  async deleteInboundIntegration(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/inbound-integrations/${id}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || 'Failed to delete inbound integration');
    }
  },

  async createInboundIntegrationKey(id: string, reference: string): Promise<{ key: string; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/inbound-integrations/${id}/keys`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ reference }),
    });
    if (!response.ok) throw new Error('Failed to create API key');
    return fetchJson(response);
  },

  async getInboundIntegrationKeys(id: string): Promise<ApiKeyInfo[]> {
    const response = await fetch(`${API_BASE_URL}/api/inbound-integrations/${id}/keys`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch API keys');
    return fetchJson(response);
  },

  async deleteInboundIntegrationKey(id: string, keyId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/inbound-integrations/${id}/keys/${keyId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to deactivate API key');
  },

  async restoreInboundIntegrationKey(id: string, keyId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/inbound-integrations/${id}/keys/${keyId}/restore`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to restore API key');
  },

  async purgeInboundIntegrationKey(id: string, keyId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/inbound-integrations/${id}/keys/${keyId}/purge`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error || body?.value?.error || 'Failed to permanently delete API key');
    }
  },

  // ── Outbound Integrations ─────────────────────────────────────────────

  async getOutboundIntegrations(): Promise<OutboundIntegration[]> {
    const response = await fetch(`${API_BASE_URL}/api/outbound-integrations`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch outbound integrations');
    return fetchJson(response);
  },

  async getDeletedOutboundIntegrations(): Promise<OutboundIntegration[]> {
    const response = await fetch(`${API_BASE_URL}/api/outbound-integrations/deleted`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch archived outbound integrations');
    return fetchJson(response);
  },

  async restoreOutboundIntegration(id: string): Promise<OutboundIntegration> {
    const response = await fetch(`${API_BASE_URL}/api/outbound-integrations/${id}/restore`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || 'Failed to restore outbound integration');
    }
    return fetchJson(response);
  },

  async createOutboundIntegration(data: CreateOutboundIntegrationRequest): Promise<OutboundIntegration> {
    const response = await fetch(`${API_BASE_URL}/api/outbound-integrations`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create outbound integration');
    return fetchJson(response);
  },

  async updateOutboundIntegration(id: string, data: UpdateOutboundIntegrationRequest): Promise<OutboundIntegration> {
    const response = await fetch(`${API_BASE_URL}/api/outbound-integrations/${id}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update outbound integration');
    return fetchJson(response);
  },

  async deleteOutboundIntegration(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/outbound-integrations/${id}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || 'Failed to delete outbound integration');
    }
  },

  async storeOutboundSecret(id: string, credential: OutboundCredential): Promise<{ secretRef: string }> {
    const response = await fetch(`${API_BASE_URL}/api/outbound-integrations/${id}/secret`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(credential),
    });
    if (!response.ok) throw new Error('Failed to store outbound integration secret');
    return fetchJson(response);
  },

  async deleteOutboundSecret(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/outbound-integrations/${id}/secret`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to delete outbound integration secret');
  },

  async getOutboundIntegrationActions(id: string): Promise<OutboundIntegrationAction[]> {
    const response = await fetch(`${API_BASE_URL}/api/outbound-integrations/${id}/actions`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch outbound integration actions');
    return fetchJson(response);
  },

  async createOutboundIntegrationAction(id: string, data: CreateOutboundIntegrationActionRequest): Promise<OutboundIntegrationAction> {
    const response = await fetch(`${API_BASE_URL}/api/outbound-integrations/${id}/actions`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to create outbound integration action');
    return fetchJson(response);
  },

  async updateOutboundIntegrationAction(id: string, actionId: string, data: UpdateOutboundIntegrationActionRequest): Promise<OutboundIntegrationAction> {
    const response = await fetch(`${API_BASE_URL}/api/outbound-integrations/${id}/actions/${actionId}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to update outbound integration action');
    return fetchJson(response);
  },

  async deleteOutboundIntegrationAction(id: string, actionId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/outbound-integrations/${id}/actions/${actionId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to delete outbound integration action');
  },

  async testOutboundIntegrationAction(
    id: string,
    actionId: string,
    data: WebhookRelayTestRequest,
  ): Promise<WebhookRelayResponse> {
    const response = await fetch(`${API_BASE_URL}/api/outbound-integrations/${id}/actions/${actionId}/test`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data ?? {}),
    });
    if (!response.ok) throw new Error('Failed to test outbound integration action');
    return fetchJson(response);
  },

  async testWorkflowWebhook(data: WorkflowWebhookTestRequest): Promise<WebhookRelayResponse> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/webhook/test`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to test workflow webhook');
    return fetchJson(response);
  },

  async validateWorkflowNotificationContent(data: WorkflowNotificationContentValidationRequest): Promise<WorkflowNotificationContentValidationResponse> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/notification-content/validate`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await buildApiError(response, 'Failed to validate workflow notification content');
      throw err;
    }
    return fetchJson(response);
  },

  // ── Routing rules ──────────────────────────────────────────────────────

  async getRoutingRules(): Promise<RoutingRule[]> {
    const response = await fetch(`${API_BASE_URL}/api/routing-rules`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch routing rules');
    return fetchJson(response);
  },

  async getRoutingRule(ruleId: string): Promise<RoutingRule> {
    const response = await fetch(`${API_BASE_URL}/api/routing-rules/${ruleId}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch routing rule');
    return fetchJson(response);
  },

  async createRoutingRule(data: CreateRoutingRuleRequest): Promise<RoutingRule> {
    const response = await fetch(`${API_BASE_URL}/api/routing-rules`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create routing rule');
    return fetchJson(response);
  },

  async updateRoutingRule(ruleId: string, data: UpdateRoutingRuleRequest): Promise<RoutingRule> {
    const response = await fetch(`${API_BASE_URL}/api/routing-rules/${ruleId}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update routing rule');
    return fetchJson(response);
  },

  async deleteRoutingRule(ruleId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/routing-rules/${ruleId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to delete routing rule');
  },

  async toggleRoutingRule(ruleId: string): Promise<{ enabled: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/routing-rules/${ruleId}/toggle`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to toggle routing rule');
    return fetchJson(response);
  },

  async reorderRoutingRules(ruleIds: string[]): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/routing-rules/reorder`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ ruleIds }),
    });
    if (!response.ok) throw new Error('Failed to reorder routing rules');
  },

  // ── Schedules ──────────────────────────────────────────────────────────

  normalizeSchedule(raw: any): Schedule {
    if (!raw) return raw as Schedule;

    const normalizedMembers = (Array.isArray(raw.members) ? raw.members : Array.isArray(raw.Members) ? raw.Members : [])
      .map((m: any) => ({
        ...m,
        userId: m.userId || m.UserId,
      }));

    return {
      ...raw,
      scheduleId: raw.scheduleId || raw.ScheduleId,
      name: raw.name || raw.Name,
      isActive: raw.isActive ?? raw.IsActive ?? true,
      timezone: raw.timezone ?? raw.timeZone ?? raw.TimeZone ?? raw.Timezone ?? 'UTC',
      version: raw.version ?? raw.Version ?? 'current',
      scheduleType: raw.scheduleType ?? raw.ScheduleType ?? 'Primary',
      primaryWindowId: raw.primaryWindowId ?? raw.PrimaryWindowId ?? undefined,
      linkedPrimaryScheduleId: raw.linkedPrimaryScheduleId ?? raw.LinkedPrimaryScheduleId ?? undefined,
      members: normalizedMembers,
      overrides: Array.isArray(raw.overrides) ? raw.overrides : Array.isArray(raw.Overrides) ? raw.Overrides : [],
    } as Schedule;
  },

  async getSchedules(): Promise<Schedule[]> {
    const response = await fetch(`${API_BASE_URL}/api/schedules`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch schedules');
    const raw = await fetchJson<any[]>(response);
    return (raw || []).map((s) => this.normalizeSchedule(s));
  },

  async getSchedule(scheduleId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}`, {
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch schedule');
    const raw = await response.json();
    return this.normalizeSchedule(unwrapResponse<any>(raw));
  },

  async getOnCallSchedule(): Promise<any> {
    const raw = await apiClient.get<any>('/api/schedules/current-oncall');

    const rawError = raw?.error || raw?.Error || raw?.message || raw?.Message;
    if (typeof rawError === 'string' && rawError.trim() !== '') {
      throw new Error(rawError);
    }

    const normalizeOnCall = (o: any) => {
      if (!o) return o;
      return {
        ...o,
        scheduleId: o.scheduleId || o.ScheduleId,
        scheduleName: o.scheduleName || o.ScheduleName,
        userId: o.userId || o.UserId,
        userName: o.userName || o.UserName,
        userEmail: o.userEmail || o.UserEmail,
        escalationUserId: o.escalationUserId || o.EscalationUserId,
        escalationUserName: o.escalationUserName || o.EscalationUserName,
        escalationUserEmail: o.escalationUserEmail || o.EscalationUserEmail,
        startTime: o.startTime || o.StartTime,
        endTime: o.endTime || o.EndTime,
      };
    };

    const currentOnCall = normalizeOnCall(raw?.currentOnCall || raw?.CurrentOnCall);
    const nextOnCall = normalizeOnCall(raw?.nextOnCall || raw?.NextOnCall);
    const allSchedulesRaw = raw?.allSchedules || raw?.AllSchedules;

    return {
      ...raw,
      currentOnCall,
      nextOnCall,
      _diagKeys: Object.keys(raw || {}).join(','),
      allSchedules: Array.isArray(allSchedulesRaw)
        ? allSchedulesRaw.map((x: any) => normalizeOnCall(x))
        : [],
    };
  },

  async createSchedule(data: Partial<CreateScheduleRequest>): Promise<Schedule> {
    const timezone = (data as any).timezone ?? (data as any).timeZone ?? (data as any).TimeZone;
    const payload = {
      ...data,
      timezone,
      scheduleType: (data as any).scheduleType ?? 'Primary',
    };

    const response = await fetch(`${API_BASE_URL}/api/schedules`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('Failed to create schedule');
    const raw = await fetchJson<any>(response);
    return this.normalizeSchedule(raw);
  },

  async updateSchedule(scheduleId: string, data: Partial<CreateScheduleRequest & { isActive: boolean }>): Promise<Schedule> {
    const timezone = (data as any).timezone ?? (data as any).timeZone ?? (data as any).TimeZone;
    const payload = timezone == null
      ? data
      : { ...data, timezone };

    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('Failed to update schedule');
    const raw = await fetchJson<any>(response);
    return this.normalizeSchedule(raw);
  },

  async deleteSchedule(scheduleId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    await readApiResult<void>(response, 'Failed to delete schedule');
  },

  async createOverride(scheduleId: string, data: { userId?: string; startTime: string; endTime: string; reason?: string }): Promise<ScheduleOverride> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/overrides`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create override');
    return fetchJson(response);
  },

  async getScheduleWindows(scheduleId: string): Promise<ScheduleWindow[]> {
    let lastError: any = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/windows`, {
          headers: authHeader(),
        });
        if (!response.ok) throw new Error('Failed to fetch schedule windows');
        return fetchJson(response);
      } catch (err) {
        lastError = err;
        if (attempt < 3) await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
      }
    }
    throw lastError || new Error('Failed to fetch schedule windows');
  },

  async createScheduleWindow(scheduleId: string, data: Partial<ScheduleWindow>): Promise<ScheduleWindow> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/windows`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      let details = '';
      try {
        details = (await response.text()).trim();
      } catch {
        details = '';
      }
      const suffix = details ? `: ${details}` : '';
      throw new Error(`Failed to create schedule window (HTTP ${response.status})${suffix}`);
    }
    return fetchJson(response);
  },

  async updateScheduleWindow(scheduleId: string, windowId: string, data: Partial<ScheduleWindow>): Promise<ScheduleWindow> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/windows/${windowId}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      let details = '';
      try {
        details = (await response.text()).trim();
      } catch {
        details = '';
      }
      const suffix = details ? `: ${details}` : '';
      throw new Error(`Failed to update schedule window (HTTP ${response.status})${suffix}`);
    }
    return fetchJson(response);
  },

  async deleteScheduleWindow(scheduleId: string, windowId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/windows/${windowId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) {
      let details = '';
      try {
        details = (await response.text()).trim();
      } catch {
        details = '';
      }
      const suffix = details ? `: ${details}` : '';
      throw new Error(`Failed to delete schedule window (HTTP ${response.status})${suffix}`);
    }
  },

  async getScheduleRotations(scheduleId: string, windowId: string): Promise<RotationConfig[]> {
    let lastError: any = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/windows/${windowId}/rotations`, {
          headers: authHeader(),
        });
        if (!response.ok) throw new Error('Failed to fetch rotations');
        return fetchJson(response);
      } catch (err) {
        lastError = err;
        if (attempt < 3) await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
      }
    }
    throw lastError || new Error('Failed to fetch rotations');
  },

  async createScheduleRotation(scheduleId: string, windowId: string, data: any): Promise<ScheduleRotation> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/windows/${windowId}/rotations`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create rotation');
    return fetchJson(response);
  },

  async updateScheduleRotation(scheduleId: string, windowId: string, rotationId: string, data: any): Promise<ScheduleRotation> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/windows/${windowId}/rotations/${rotationId}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update rotation');
    return fetchJson(response);
  },

  async deleteScheduleRotation(scheduleId: string, windowId: string, rotationId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/windows/${windowId}/rotations/${rotationId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to delete rotation');
  },

  async putScheduleRoster(scheduleId: string, rotationId: string, userIds: string[]): Promise<ScheduleRosterEntry[]> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/rotations/${rotationId}/roster`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify({ userIds }),
    });
    if (!response.ok) throw new Error('Failed to save roster');
    return fetchJson(response);
  },

  async removeScheduleRosterMember(scheduleId: string, rotationId: string, request: RosterRemovalRequest): Promise<RosterRemovalResult> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/rotations/${rotationId}/roster/remove`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(request),
    });
    return readApiResult<RosterRemovalResult>(response, request.preview ? 'Failed to preview roster removal' : 'Failed to remove roster member');
  },

  async redeployScheduleRoster(scheduleId: string, rotationId: string, startUserId: string, effectiveFromUtc: string, reason?: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/rotations/${rotationId}/redeploy`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ startUserId, effectiveFromUtc, reason }),
    });
    if (!response.ok) throw new Error('Failed to redeploy roster');
  },

  async getScheduleTimeline(scheduleId: string, startUtc: string, endUtc: string): Promise<{ entries: ScheduleTimelineEntry[] }> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/timeline?startUtc=${encodeURIComponent(startUtc)}&endUtc=${encodeURIComponent(endUtc)}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch schedule timeline');
    const raw = await fetchJson<any>(response);
    const rawEntries = raw?.entries || raw?.Entries;
    const entries = Array.isArray(rawEntries)
      ? rawEntries.map((e: any) => ({
          rotationId: e.rotationId || e.RotationId,
          rotationName: e.rotationName || e.RotationName,
          windowStartUtc: e.windowStartUtc || e.WindowStartUtc || e.startUtc || e.StartUtc,
          startUtc: e.startUtc || e.StartUtc,
          endUtc: e.endUtc || e.EndUtc,
          userId: e.userId || e.UserId,
          userName: e.userName || e.UserName,
          source: e.source || e.Source,
          sourceId: e.sourceId ?? e.SourceId ?? null,
        }))
      : [];
    return { entries };
  },

  async getScheduleCovers(scheduleId: string, filter: { from?: string; to?: string; status?: ScheduleCoverStatus } = {}): Promise<ScheduleCover[]> {
    const params = new URLSearchParams();
    if (filter.from) params.set('from', filter.from);
    if (filter.to) params.set('to', filter.to);
    if (filter.status) params.set('status', filter.status);
    const query = params.toString();
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/covers${query ? `?${query}` : ''}`, {
      headers: authHeader(),
    });
    return (await readApiResult<ScheduleCover[]>(response, 'Failed to load covers')) || [];
  },

  async suggestScheduleCoverBlocks(scheduleId: string, coveredUserId: string, rotationId?: string | null): Promise<ScheduleCoverBlock[]> {
    const params = new URLSearchParams({ coveredUserId });
    if (rotationId) params.set('rotationId', rotationId);
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/covers/suggest?${params.toString()}`, {
      headers: authHeader(),
    });
    return (await readApiResult<ScheduleCoverBlock[]>(response, 'Failed to load upcoming blocks')) || [];
  },

  async createScheduleCover(scheduleId: string, request: ScheduleCoverRequest): Promise<ScheduleCoverCreateResult> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/covers`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(request),
    });
    return readApiResult<ScheduleCoverCreateResult>(response, 'Failed to create cover');
  },

  async cancelScheduleCover(scheduleId: string, coverId: string): Promise<ScheduleCover> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/covers/${coverId}/cancel`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    return readApiResult<ScheduleCover>(response, 'Failed to cancel cover');
  },

  async endScheduleCover(scheduleId: string, coverId: string): Promise<ScheduleCover> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/covers/${coverId}/end`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    return readApiResult<ScheduleCover>(response, 'Failed to end cover');
  },

  async getTeamOverrides(bucket: TeamOverrideBucket): Promise<TeamOverride[]> {
    const response = await fetch(`${API_BASE_URL}/api/oncall-overrides?status=${encodeURIComponent(bucket)}`, {
      headers: authHeader(),
    });
    return (await readTeamOverrideResult<TeamOverride[]>(response, 'Failed to load on-call overrides')) || [];
  },

  async getTeamOverrideActiveSummary(): Promise<TeamOverrideActiveSummary> {
    const response = await fetch(`${API_BASE_URL}/api/oncall-overrides/active-summary`, {
      headers: authHeader(),
    });
    const summary = await readTeamOverrideResult<TeamOverrideActiveSummary>(response, 'Failed to load active overrides');
    return { count: summary?.count ?? 0, canManage: !!summary?.canManage, overrides: summary?.overrides || [] };
  },

  async createTeamOverride(request: TeamOverrideRequest): Promise<{ override: TeamOverride; groupMemberCount?: number | null }> {
    const response = await fetch(`${API_BASE_URL}/api/oncall-overrides`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(request),
    });
    return readTeamOverrideResult(response, 'Failed to create on-call override');
  },

  async endTeamOverride(overrideId: string): Promise<TeamOverride> {
    const response = await fetch(`${API_BASE_URL}/api/oncall-overrides/${overrideId}/end`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    return readTeamOverrideResult<TeamOverride>(response, 'Failed to end on-call override');
  },

  async cancelTeamOverride(overrideId: string): Promise<TeamOverride> {
    const response = await fetch(`${API_BASE_URL}/api/oncall-overrides/${overrideId}/cancel`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    return readTeamOverrideResult<TeamOverride>(response, 'Failed to cancel on-call override');
  },

  /** Presence of current and next on-call operators; batches beyond the server's 50-schedule limit. */
  async getOnCallPresence(scheduleIds: string[]): Promise<OnCallPresenceResponse> {
    const ids = Array.from(new Set(scheduleIds.filter(Boolean)));
    const merged: OnCallPresenceResponse = { generatedAtUtc: new Date().toISOString(), staleAfterMinutes: 0, schedules: [] };
    for (let i = 0; i < ids.length; i += PRESENCE_SCHEDULE_BATCH) {
      const batch = ids.slice(i, i + PRESENCE_SCHEDULE_BATCH);
      const response = await fetch(`${API_BASE_URL}/api/oncall/presence?scheduleIds=${batch.map(encodeURIComponent).join(',')}`, {
        headers: authHeader(),
      });
      const part = await readApiResult<OnCallPresenceResponse>(response, 'Failed to load on-call presence');
      if (!part) continue;
      merged.generatedAtUtc = part.generatedAtUtc || merged.generatedAtUtc;
      merged.staleAfterMinutes = part.staleAfterMinutes || merged.staleAfterMinutes;
      merged.schedules.push(...(part.schedules || []));
    }
    return merged;
  },

  async getOperationalPresence(scheduleId?: string): Promise<OperationalPresenceResponse> {
    const query = scheduleId ? `?scheduleIds=${encodeURIComponent(scheduleId)}` : '';
    const response = await fetch(`${API_BASE_URL}/api/oncall/presence/operational${query}`, { headers: authHeader() });
    return readApiResult<OperationalPresenceResponse>(response, 'Failed to load operational presence');
  },

  async getPresenceIncidents(status: PresenceIncidentStatus | 'all' = 'Open', take = 50): Promise<PresenceIncidentDto[]> {
    const response = await fetch(`${API_BASE_URL}/api/oncall/presence/incidents?status=${encodeURIComponent(status)}&take=${take}`, {
      headers: authHeader(),
    });
    return (await readApiResult<PresenceIncidentDto[]>(response, 'Failed to load presence incidents')) || [];
  },

  async getPresenceSettings(): Promise<PresenceSettings> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/presence-settings`, { headers: authHeader() });
    return readApiResult<PresenceSettings>(response, 'Failed to load presence settings');
  },

  async updatePresenceSettings(enabled: boolean): Promise<PresenceSettings> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/presence-settings`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify({ enabled }),
    });
    return readApiResult<PresenceSettings>(response, 'Failed to update presence settings');
  },

  async getScheduleGaps(scheduleId: string, startUtc: string, endUtc: string): Promise<{ gaps: ScheduleGap[] }> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/gaps?startUtc=${encodeURIComponent(startUtc)}&endUtc=${encodeURIComponent(endUtc)}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch schedule gaps');
    return fetchJson(response);
  },

  async getScheduleSwaps(scheduleId: string, status?: string): Promise<ScheduleShiftSwap[]> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/swaps${query}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch schedule swaps');
    return fetchJson(response);
  },

  async createScheduleSwap(scheduleId: string, data: {
    rotationId?: string;
    userAId: string;
    userBId: string;
    startUtc: string;
    endUtc: string;
    reason?: string;
    isOneTime?: boolean;
  }): Promise<ScheduleShiftSwap> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/swaps`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create swap');
    return fetchJson(response);
  },

  async approveScheduleSwap(scheduleId: string, swapId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/swaps/${swapId}/approve`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to approve swap');
  },

  async rejectScheduleSwap(scheduleId: string, swapId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/swaps/${swapId}/reject`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to reject swap');
  },

  async cancelScheduleSwap(scheduleId: string, swapId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/swaps/${swapId}/cancel`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to cancel swap');
  },

  async revertScheduleSwap(scheduleId: string, swapId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/swaps/${swapId}/revert`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to revert swap');
  },

  async applyScheduleSwap(scheduleId: string, swapId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/schedules/${scheduleId}/swaps/${swapId}/apply`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to apply swap');
  },

  async getScheduleTimelineRange(scheduleId: string, spanDays: number): Promise<{ entries: ScheduleTimelineEntry[] }> {
    const now = new Date();
    const end = new Date(now.getTime() + Math.max(1, spanDays) * 86400000);
    return this.getScheduleTimeline(scheduleId, now.toISOString(), end.toISOString());
  },

  // ── Rules ──────────────────────────────────────────────────────────────

  async getRules(): Promise<Rule[]> {
    const response = await fetch(`${API_BASE_URL}/api/rules`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch rules');
    return fetchJson(response);
  },

  async getRule(ruleId: string): Promise<Rule> {
    const response = await fetch(`${API_BASE_URL}/api/rules/${ruleId}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch rule');
    return fetchJson(response);
  },

  async createRule(data: CreateRuleRequest): Promise<Rule> {
    const response = await fetch(`${API_BASE_URL}/api/rules`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create rule');
    return fetchJson(response);
  },

  async updateRule(ruleId: string, data: Partial<CreateRuleRequest>): Promise<Rule> {
    const response = await fetch(`${API_BASE_URL}/api/rules/${ruleId}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update rule');
    return fetchJson(response);
  },

  async deleteRule(ruleId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/rules/${ruleId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to delete rule');
  },

  async saveRuleSecret(ruleId: string, name: string, value: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/rules/${ruleId}/secrets`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ name, value }),
    });
    if (!response.ok) throw new Error('Failed to save secret');
  },

  // ── Escalation Policies ────────────────────────────────────────────────

  async getEscalationPolicies(): Promise<EscalationPolicyDto[]> {
    const response = await fetch(`${API_BASE_URL}/api/escalation-policies`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch escalation policies');
    return fetchJson(response);
  },

  async getEscalationPolicy(policyId: string): Promise<EscalationPolicyDto> {
    const response = await fetch(`${API_BASE_URL}/api/escalation-policies/${policyId}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch escalation policy');
    return fetchJson(response);
  },

  async createEscalationPolicy(data: CreateEscalationPolicyRequest): Promise<EscalationPolicyDto> {
    const response = await fetch(`${API_BASE_URL}/api/escalation-policies`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create escalation policy');
    return fetchJson(response);
  },

  async updateEscalationPolicy(policyId: string, data: UpdateEscalationPolicyRequest): Promise<EscalationPolicyDto> {
    const response = await fetch(`${API_BASE_URL}/api/escalation-policies/${policyId}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update escalation policy');
    return fetchJson(response);
  },

  async deleteEscalationPolicy(policyId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/escalation-policies/${policyId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to delete escalation policy');
  },

  // ── Ingestion health ──────────────────────────────────────────────────

  // ── Notification Channels ─────────────────────────────────────────────

  async getTenantSmsGateways(): Promise<SmsGateway[]> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/sms-gateways`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch tenant SMS gateways');
    const data = await fetchJson<{ gateways: SmsGateway[] }>(response);
    return Array.isArray(data?.gateways) ? data.gateways : [];
  },

  async createTenantSmsGateway(data: Omit<CreateSmsGatewayRequest, 'scopeType' | 'tenantId'>): Promise<SmsGateway> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/sms-gateways`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create tenant SMS gateway');
    return fetchJson(response);
  },

  async updateTenantSmsGateway(gatewayId: string, data: Omit<UpdateSmsGatewayRequest, 'scopeType' | 'tenantId'>): Promise<SmsGateway> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/sms-gateways/${gatewayId}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update tenant SMS gateway');
    return fetchJson(response);
  },

  async deleteTenantSmsGateway(gatewayId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/sms-gateways/${gatewayId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to delete tenant SMS gateway');
  },

  async generateTenantSmsGatewayOnboardingPayload(
    gatewayId: string,
    request: SmsGatewayOnboardingPayloadRequest,
  ): Promise<SmsGatewayOnboardingPayloadResponse> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/sms-gateways/${gatewayId}/onboarding-payload`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to generate SMS gateway onboarding payload');
    return fetchJson(response);
  },

  async getTenantSmsDispatchMessages(params?: { take?: number; skip?: number; status?: string; gatewayId?: string }): Promise<SmsDispatchMessage[]> {
    const query = new URLSearchParams();
    if (params?.take != null) query.set('take', String(params.take));
    if (params?.skip != null) query.set('skip', String(params.skip));
    if (params?.status) query.set('status', params.status);
    if (params?.gatewayId) query.set('gatewayId', params.gatewayId);

    const response = await fetch(`${API_BASE_URL}/api/tenant/sms-dispatch/messages?${query.toString()}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch tenant SMS dispatch messages');
    const data = await fetchJson<{ messages: SmsDispatchMessage[] }>(response);
    return Array.isArray(data?.messages) ? data.messages : [];
  },

  async enqueueTenantSmsDispatchMessage(data: CreateSmsDispatchMessageRequest): Promise<SmsDispatchMessage> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/sms-dispatch/messages`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to enqueue SMS dispatch message');
    return fetchJson(response);
  },

  async dequeueTenantSmsDispatchMessage(dispatchMessageId: string): Promise<SmsDispatchMessage> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/sms-dispatch/messages/${dispatchMessageId}/dequeue`, {
      method: 'POST',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to dequeue SMS dispatch message');
    return fetchJson(response);
  },

  async getNotificationChannels(): Promise<NotificationChannel[]> {
    const response = await fetch(`${API_BASE_URL}/api/notification-channels`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch notification channels');
    return fetchJson(response);
  },

  async getExchangeMailboxes(skip = 0, take = 100): Promise<ExchangeMailboxRegistration[]> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/mailboxes?skip=${skip}&take=${take}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error(`Failed to fetch registered mailboxes (HTTP ${response.status})`);
    return fetchJson(response);
  },

  async createExchangeMailbox(data: { mailboxObjectId: string; name: string }): Promise<ExchangeMailboxRegistration> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/mailboxes`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to register mailbox');
    return fetchJson(response);
  },

  async updateExchangeMailbox(mailboxId: string, data: { revision: number; name: string; isEnabled: boolean }): Promise<ExchangeMailboxRegistration> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/mailboxes/${mailboxId}`, {
      method: 'PUT', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to update mailbox');
    return fetchJson(response);
  },

  async retireExchangeMailbox(mailboxId: string, revision: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/mailboxes/${mailboxId}/retire`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ revision }),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to retire mailbox');
  },

  async getExchangeMailboxSenders(mailboxId: string, skip = 0, take = 100): Promise<ExchangeSenderRegistration[]> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/mailboxes/${mailboxId}/senders?skip=${skip}&take=${take}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error(`Failed to fetch send addresses (HTTP ${response.status})`);
    return fetchJson(response);
  },

  async createExchangeMailboxSender(mailboxId: string, data: { label: string; fromAddress: string; displayName?: string }): Promise<ExchangeSenderRegistration> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/mailboxes/${mailboxId}/senders`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to register send address');
    return fetchJson(response);
  },

  async recordExchangeSenderPrerequisiteEvidence(senderId: string, data: {
    revision: number;
    sharedMailboxAttested: boolean;
    addressMembershipAttested: boolean;
    applicationMailSendAttested: boolean;
    aliasPreservationAttested: boolean;
  }): Promise<ExchangeSenderRegistration> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/senders/${encodeURIComponent(senderId)}/evidence`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to record sender prerequisites');
    return fetchJson(response);
  },

  async startExchangeSenderTest(senderId: string, data: {
    revision: number;
    mailboxRevision: number;
    idempotencyKey: string;
    recipient: string;
    confirmRecipient: boolean;
  }): Promise<ExchangeSenderTestOperation> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/senders/${encodeURIComponent(senderId)}/test`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to start sender test');
    return fetchJson(response);
  },

  async getExchangeSenderTestOperation(operationId: string): Promise<ExchangeSenderTestOperation> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/sender-tests/${encodeURIComponent(operationId)}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to load sender test status');
    return fetchJson(response);
  },

  async getLatestAcceptedExchangeSenderTest(senderId: string, revision: number): Promise<ExchangeSenderTestOperation> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/senders/${encodeURIComponent(senderId)}/latest-accepted-test?revision=${revision}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw await buildApiError(response, 'No accepted controlled test is available for this sender revision');
    return fetchJson(response);
  },

  async confirmExchangeSenderAlias(senderId: string, data: {
    revision: number;
    testOperationId: string;
    observedFromAddress: string;
    observedSenderAddress: string;
    observedReplyToAddress: string;
    confirmReceivedMessage: boolean;
    noOnBehalfOfConfirmed: boolean;
  }): Promise<ExchangeSenderRegistration> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/senders/${encodeURIComponent(senderId)}/alias-confirmation`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to confirm sender alias behavior');
    return fetchJson(response);
  },

  async updateExchangeSender(senderId: string, data: { revision: number; label: string; displayName?: string; isEnabled: boolean }): Promise<ExchangeSenderRegistration> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/senders/${senderId}`, {
      method: 'PUT', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to update send address');
    return fetchJson(response);
  },

  async retireExchangeSender(senderId: string, revision: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/senders/${senderId}/retire`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ revision }),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to retire send address');
  },

  async getEligibleExchangeSenders(skip = 0, take = 100): Promise<ExchangeSenderRegistration[]> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/senders/eligible?skip=${skip}&take=${take}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to fetch eligible Graph senders');
    return fetchJson(response);
  },

  async getNotificationDeliveryFailures(skip = 0, take = 50, filters: NotificationDeliveryFailureFilters = {}): Promise<NotificationDeliveryFailure[]> {
    const params = new URLSearchParams({ skip: String(skip), take: String(take) });
    if (filters.status) params.set('status', filters.status);
    if (filters.channelType) params.set('channelType', filters.channelType);
    if (filters.deliveryProvider) params.set('provider', filters.deliveryProvider);
    if (filters.mailboxName) params.set('mailboxName', filters.mailboxName);
    if (filters.updatedAfterUtc) params.set('updatedAfterUtc', filters.updatedAfterUtc);
    if (filters.updatedBeforeUtc) params.set('updatedBeforeUtc', filters.updatedBeforeUtc);
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/deliveries?${params.toString()}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to load notification delivery failures');
    return fetchJson(response);
  },

  async getNotificationDelivery(requestId: string): Promise<NotificationDeliveryFailure> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/deliveries/${encodeURIComponent(requestId)}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to load notification delivery');
    return fetchJson(response);
  },

  async getNotificationDeliveryAttempts(requestId: string, skip = 0, take = 50): Promise<NotificationDeliveryAttempt[]> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/deliveries/${encodeURIComponent(requestId)}/attempts?skip=${skip}&take=${take}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to load notification delivery attempts');
    return fetchJson(response);
  },

  async requestNotificationDeliveryReplay(requestId: string, data: { idempotencyKey: string; reason: string; confirmDuplicateRisk: boolean }): Promise<{ replayCommandId: string; state: string }> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/deliveries/${encodeURIComponent(requestId)}/resubmissions`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to request notification delivery replay');
    return fetchJson(response);
  },

  async requestBulkNotificationDeliveryReplay(data: { operationId: string; requestIds: string[]; reason: string; confirmDuplicateRisk: boolean }): Promise<BulkNotificationReplayResponse> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/deliveries/resubmissions/bulk`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to request bulk notification delivery replay');
    return fetchJson(response);
  },

  async getBulkNotificationDeliveryReplay(operationId: string): Promise<BulkNotificationReplayResponse> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/delivery-replay-bulks/${encodeURIComponent(operationId)}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to load bulk replay operation');
    return fetchJson(response);
  },

  async resumeBulkNotificationDeliveryReplay(operationId: string): Promise<BulkNotificationReplayResponse> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/delivery-replay-bulks/${encodeURIComponent(operationId)}/resume`, {
      method: 'POST', headers: jsonHeaders(), body: '{}',
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to resume bulk replay operation');
    return fetchJson(response);
  },

  async getRecentBulkNotificationDeliveryReplays(take = 10): Promise<BulkNotificationReplaySummary[]> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/delivery-replay-bulks?take=${take}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to load recent bulk replay operations');
    return fetchJson(response);
  },

  async cancelNotificationReplayOperation(replayCommandId: string, data: { expectedPublishAttemptCount: number; reason: string }): Promise<{ replayCommandId: string; state: string }> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/delivery-replays/${encodeURIComponent(replayCommandId)}/cancellation`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to cancel pending replay');
    return fetchJson(response);
  },

  async getNotificationReplayOperation(replayCommandId: string): Promise<NotificationReplayOperation> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/delivery-replays/${encodeURIComponent(replayCommandId)}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to load replay operation');
    return fetchJson(response);
  },

  async getNotificationDeliveryIncidents(options?: { take?: number; skip?: number; criticalOnly?: boolean; includeArchived?: boolean }): Promise<NotificationDeliveryIncident[]> {
    const params = new URLSearchParams();
    params.set('take', String(options?.take ?? 50));
    if (options?.criticalOnly) params.set('criticalOnly', 'true');
    if (options?.includeArchived) params.set('includeArchived', 'true');
    if (options?.skip) params.set('skip', String(options.skip));
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/incidents?${params.toString()}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to load delivery incidents');
    const result = await fetchIncidentJson<NotificationDeliveryIncident[] | { error?: string }>(response);
    if (!Array.isArray(result)) throw new Error(result?.error || 'Invalid delivery incident response.');
    return result;
  },

  async acknowledgeNotificationDeliveryIncident(incidentId: string): Promise<{ acknowledged: boolean; incidentId: string }> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/incidents/${encodeURIComponent(incidentId)}/acknowledgements`, {
      method: 'POST', headers: jsonHeaders(), body: '{}',
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to acknowledge delivery incident');
    return fetchIncidentJson(response);
  },

  async resolveNotificationDeliveryIncident(incidentId: string, reason: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/incidents/${encodeURIComponent(incidentId)}/resolve`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ reason }),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to resolve delivery incident');
    const result = await fetchIncidentJson<{ error?: string }>(response);
    if (result?.error) throw new Error(result.error);
  },

  async archiveNotificationDeliveryIncident(incidentId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/incidents/${encodeURIComponent(incidentId)}/archive`, {
      method: 'POST', headers: jsonHeaders(), body: '{}',
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to archive delivery incident');
    const result = await fetchIncidentJson<{ error?: string }>(response);
    if (result?.error) throw new Error(result.error);
  },

  async retestNotificationDeliveryIncident(incidentId: string, recipient: string): Promise<{ requestId: string }> {
    const response = await fetch(`${API_BASE_URL}/api/tenant/email/incidents/${encodeURIComponent(incidentId)}/retest`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ recipientEmail: recipient, confirmed: true }),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to queue channel retest');
    const result = await fetchIncidentJson<{ requestId?: string; error?: string }>(response);
    if (result?.error || !result?.requestId) throw new Error(result?.error || 'Invalid channel retest response.');
    return { requestId: result.requestId };
  },

  async getPlatformNotificationIncidentSummary(): Promise<NotificationPlatformIncidentSummary> {
    const response = await fetch(`${API_BASE_URL}/api/platform/email/incidents/critical-summary`, {
      headers: authHeader(),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to load platform incident summary');
    return fetchJson(response);
  },

  async getNotificationChannel(channelId: string): Promise<NotificationChannel> {
    const response = await fetch(`${API_BASE_URL}/api/notification-channels/${channelId}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch notification channel');
    return fetchJson(response);
  },

  async createNotificationChannel(data: CreateNotificationChannelRequest): Promise<NotificationChannel> {
    const response = await fetch(`${API_BASE_URL}/api/notification-channels`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      let details = '';
      try {
        details = (await response.text()).trim();
      } catch {
        details = '';
      }
      const suffix = details ? `: ${details}` : '';
      throw new Error(`Failed to create notification channel (HTTP ${response.status})${suffix}`);
    }
    return fetchJson(response);
  },

  async updateNotificationChannel(channelId: string, data: UpdateNotificationChannelRequest): Promise<NotificationChannel> {
    const response = await fetch(`${API_BASE_URL}/api/notification-channels/${channelId}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update notification channel');
    return fetchJson(response);
  },

  async deleteNotificationChannel(channelId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/notification-channels/${channelId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to delete notification channel');
  },

  async toggleNotificationChannel(channelId: string): Promise<{ isEnabled: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/notification-channels/${channelId}/toggle`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to toggle notification channel');
    return fetchJson(response);
  },

  async testNotificationChannel(channelId: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/notification-channels/${channelId}/test`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to test notification channel');
    return fetchJson(response);
  },

  // ── User Notification Preferences ─────────────────────────────────────

  async getUserNotificationPreferences(): Promise<UserNotificationPreference[]> {
    const response = await fetch(`${API_BASE_URL}/api/users/me/notification-preferences`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch notification preferences');
    return fetchJson(response);
  },

  async updateUserNotificationPreferences(prefs: Array<{ method: string; priority: number; isEnabled: boolean; configJson?: string }>): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/users/me/notification-preferences`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(prefs),
    });
    if (!response.ok) throw new Error('Failed to update notification preferences');
  },

  // ── Shift reminder preferences (BL-043 BE-089) ────────────────────────

  async getShiftReminderPreferences(): Promise<ShiftReminderPreferences> {
    const response = await fetch(`${API_BASE_URL}/api/users/me/shift-reminder-preferences`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch shift reminder preferences');
    return fetchJson(response);
  },

  async updateShiftReminderPreferences(update: ShiftReminderPreferencesUpdate): Promise<ShiftReminderPreferences> {
    const response = await fetch(`${API_BASE_URL}/api/users/me/shift-reminder-preferences`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(update),
    });
    if (!response.ok) throw new Error('Failed to update shift reminder preferences');
    return fetchJson(response);
  },

  async getNotifications(options?: { scope?: 'all' | NotificationScope; unreadOnly?: boolean; take?: number }): Promise<NotificationInboxItem[]> {
    const qs = new URLSearchParams();
    if (options?.scope) qs.set('scope', options.scope);
    if (typeof options?.unreadOnly === 'boolean') qs.set('unreadOnly', options.unreadOnly ? 'true' : 'false');
    if (typeof options?.take === 'number') qs.set('take', String(options.take));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';

    const response = await fetch(`${API_BASE_URL}/api/notifications${suffix}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch notifications');
    const payload = await fetchJson<any>(response);
    return normalizeNotificationInboxItems(payload).filter(item =>
      String(item.sourceType ?? (item as any).SourceType ?? '').trim().toLowerCase() !== 'inbound');
  },

  async getInboundNotifications(options?: { scope?: 'all' | NotificationScope; unreadOnly?: boolean; take?: number }): Promise<NotificationInboxItem[]> {
    const qs = new URLSearchParams();
    if (options?.scope) qs.set('scope', options.scope);
    if (typeof options?.unreadOnly === 'boolean') qs.set('unreadOnly', String(options.unreadOnly));
    if (typeof options?.take === 'number') qs.set('take', String(options.take));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await fetch(`${API_BASE_URL}/api/inbound-notifications${suffix}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch inbound notifications');
    return normalizeNotificationInboxItems(await fetchJson<any>(response));
  },

  async getInboundNotificationsPage(options?: { scope?: 'all' | NotificationScope; unreadOnly?: boolean; take?: number; cursor?: string | null;
    status?: string; severity?: string; service?: string; search?: string; startUtc?: string; endUtc?: string }): Promise<InboundNotificationPage> {
    const qs = new URLSearchParams();
    if (options?.scope) qs.set('scope', options.scope);
    if (typeof options?.unreadOnly === 'boolean') qs.set('unreadOnly', String(options.unreadOnly));
    if (options?.status) qs.set('status', options.status);
    if (options?.severity) qs.set('severity', options.severity);
    if (options?.service) qs.set('service', options.service);
    if (options?.search) qs.set('search', options.search);
    if (options?.startUtc) qs.set('startUtc', options.startUtc);
    if (options?.endUtc) qs.set('endUtc', options.endUtc);
    if (typeof options?.take === 'number') qs.set('take', String(options.take));
    if (options?.cursor) qs.set('cursor', options.cursor);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await fetch(`${API_BASE_URL}/api/inbound-notifications/page${suffix}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch inbound notifications page');
    const payload = await fetchJson<any>(response);
    return {
      items: normalizeNotificationInboxItems(payload?.items ?? payload?.Items),
      limit: Number(payload?.limit ?? payload?.Limit ?? options?.take ?? 50),
      nextCursor: payload?.nextCursor ?? payload?.NextCursor ?? null,
      total: Number(payload?.total ?? payload?.Total ?? 0),
      availableServices: payload?.availableServices ?? payload?.AvailableServices ?? [],
      availableStatuses: payload?.availableStatuses ?? payload?.AvailableStatuses ?? [],
    };
  },

  async getInboundNotificationDetail(notificationId: string, signal?: AbortSignal): Promise<InboundNotificationDetail | null> {
    const response = await fetch(`${API_BASE_URL}/api/notifications/${encodeURIComponent(notificationId)}/detail`, {
      headers: authHeader(), signal,
    });
    if (response.status === 404) return null;
    if (!response.ok) throw await buildApiError(response, 'Failed to fetch notification details');
    const raw = await response.json();
    const wrapped = raw && typeof raw === 'object' && ('Value' in raw || 'value' in raw);
    const status = wrapped ? (raw.StatusCode ?? raw.statusCode) : response.status;
    if (status === 404) return null;
    if (status >= 400) throw new Error(`Failed to fetch notification details (${status})`);
    const detail = wrapped ? (raw.Value ?? raw.value) : raw;
    if (!detail || typeof detail.notificationId !== 'string' || !Array.isArray(detail.timeline)) {
      throw new Error('Invalid notification detail response');
    }
    return detail as InboundNotificationDetail;
  },

  async getNotificationEmailHistory(notificationId: string, skip = 0, signal?: AbortSignal): Promise<NotificationEmailHistoryPage> {
    const response = await fetch(`${API_BASE_URL}/api/notifications/${encodeURIComponent(notificationId)}/email-deliveries?skip=${skip}&take=50`, {
      headers: authHeader(), signal,
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to fetch email submission history');
    const raw = await response.json();
    const wrapped = raw && typeof raw === 'object' && ('Value' in raw || 'value' in raw);
    const status = wrapped ? (raw.StatusCode ?? raw.statusCode) : response.status;
    if (status >= 400) throw new Error(`Failed to fetch email submission history (${status})`);
    const result = wrapped ? (raw.Value ?? raw.value) : raw;
    if (!result || !Array.isArray(result.items) || !(result.nextSkip === null || Number.isInteger(result.nextSkip)))
      throw new Error('Invalid email submission history response');
    return result;
  },

  async getNotificationsUnreadCount(): Promise<number> {
    const response = await fetch(`${API_BASE_URL}/api/notifications/unread-count`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch notifications unread count');
    const payload = await fetchJson<{ unreadCount?: number }>(response);
    return Number(payload?.unreadCount || 0);
  },

  async markNotificationRead(notificationId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/notifications/${encodeURIComponent(notificationId)}/read`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({}),
    });
    if (!response.ok) throw new Error('Failed to mark notification as read');
  },

  async closeInboundNotification(notificationId: string, reason?: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/inbound-notifications/${encodeURIComponent(notificationId)}/close`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ reason }),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to close notification');
    const raw = await response.json();
    const payload = raw?.Value ?? raw?.value ?? raw;
    const status = raw?.StatusCode ?? raw?.statusCode ?? response.status;
    if (status >= 400 || payload?.success !== true) {
      throw new Error(payload?.error || 'Failed to close notification');
    }
  },

  async replayInboundNotification(notificationId: string, eventId?: string, receiptId?: string): Promise<{ replayId: string }> {
    if (!!eventId === !!receiptId) throw new Error('Provide exactly one timeline event id or schema receipt id');
    const response = await fetch(`${API_BASE_URL}/api/notifications/${encodeURIComponent(notificationId)}/replay`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify(eventId ? { eventId } : { receiptId }),
    });
    if (!response.ok) throw await buildApiError(response, 'Failed to replay notification payload');
    const raw = await response.json();
    const payload = raw?.Value ?? raw?.value ?? raw;
    const status = raw?.StatusCode ?? raw?.statusCode ?? response.status;
    if (status >= 400 || payload?.success !== true || typeof payload?.replayId !== 'string')
      throw new Error(payload?.error || 'Failed to replay notification payload');
    return { replayId: payload.replayId };
  },

  async clearNotification(notificationId: string, scope?: NotificationScope): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/notifications/${encodeURIComponent(notificationId)}/clear`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ scope }),
    });
    if (!response.ok) throw new Error('Failed to clear notification');
  },

  async clearNotifications(options?: { scope?: 'all' | NotificationScope; unreadOnly?: boolean; take?: number }): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/notifications/clear`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({
        scope: options?.scope,
        unreadOnly: options?.unreadOnly,
        take: options?.take,
      }),
    });
    if (!response.ok) throw new Error('Failed to clear notifications');
  },

  async getIngestionHealth(): Promise<IngestionHealthResponse> {
    const response = await fetch(`${API_BASE_URL}/api/ingestion/health`, {
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch ingestion health');
    const raw = await response.json();
    return unwrapResponse<IngestionHealthResponse>(raw);
  },

  async getIngestionFailures(): Promise<{ failures: IngestionFailure[] }> {
    const response = await fetch(`${API_BASE_URL}/api/ingestion/health/failures`, {
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch ingestion failures');
    const raw = await response.json();
    return unwrapResponse<{ failures: IngestionFailure[] }>(raw);
  },

  async getUnmatchedInboundEvents(): Promise<{ events: UnmatchedInboundEvent[] }> {
    const response = await fetch(`${API_BASE_URL}/api/ingestion/health/unmatched`, {
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch unmatched inbound events');
    const raw = await response.json();
    return unwrapResponse<{ events: UnmatchedInboundEvent[] }>(raw);
  },

  // ── Workflows ─────────────────────────────────────────────────────────

  async getWorkflows(): Promise<WorkflowDto[]> {
    const response = await fetch(`${API_BASE_URL}/api/workflows`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch workflows');
    return fetchJson(response);
  },

  async getWorkflow(workflowId: string): Promise<WorkflowDto> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${workflowId}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch workflow');
    return fetchJson(response);
  },

  async getWorkflowConditionMetadata(): Promise<WorkflowConditionMetadata> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/condition-metadata`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch workflow condition metadata');
    return fetchJson(response);
  },

  async getSharedSubWorkflows(): Promise<SubWorkflowDto[]> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/sub-workflows`, { headers: authHeader() });
    if (!response.ok) throw new Error('Failed to fetch sub-workflows');
    return fetchJson(response);
  },

  async getSharedSubWorkflow(workflowId: string): Promise<SubWorkflowDto> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/sub-workflows/${workflowId}`, { headers: authHeader() });
    if (!response.ok) throw new Error('Failed to fetch sub-workflow');
    return fetchJson(response);
  },

  async getPrivateSubWorkflows(ownerWorkflowId: string): Promise<SubWorkflowDto[]> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${ownerWorkflowId}/sub-workflows`, { headers: authHeader() });
    if (!response.ok) throw new Error('Failed to fetch private sub-workflows');
    return fetchJson(response);
  },

  async createPrivateSubWorkflow(ownerWorkflowId: string, data: CreatePrivateSubWorkflowRequest): Promise<SubWorkflowDto> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${ownerWorkflowId}/sub-workflows`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || `Failed to create private sub-workflow (HTTP ${response.status})`);
    }
    return fetchJson(response);
  },

  async updatePrivateSubWorkflow(ownerWorkflowId: string, workflowId: string, data: UpdateSubWorkflowRequest): Promise<SubWorkflowDto> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${ownerWorkflowId}/sub-workflows/${workflowId}`, {
      method: 'PUT', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || `Failed to update private sub-workflow (HTTP ${response.status})`);
    }
    return fetchJson(response);
  },

  async deletePrivateSubWorkflow(ownerWorkflowId: string, workflowId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${ownerWorkflowId}/sub-workflows/${workflowId}`, {
      method: 'DELETE', headers: authHeader(),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || `Failed to delete private sub-workflow (HTTP ${response.status})`);
    }
  },

  async cloneSharedSubWorkflowAsPrivate(ownerWorkflowId: string, sharedWorkflowId: string, name: string): Promise<SubWorkflowDto> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${ownerWorkflowId}/sub-workflows/${sharedWorkflowId}/clone`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ name }),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || `Failed to clone sub-workflow (HTTP ${response.status})`);
    }
    return fetchJson(response);
  },

  async convertPrivateSubWorkflowToShared(ownerWorkflowId: string, privateWorkflowId: string,
    data: ConvertPrivateSubWorkflowRequest): Promise<SubWorkflowDto> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${ownerWorkflowId}/sub-workflows/${privateWorkflowId}/convert-to-shared`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || `Failed to convert sub-workflow (HTTP ${response.status})`);
    }
    return fetchJson(response);
  },

  async createSharedSubWorkflow(data: CreateSubWorkflowRequest): Promise<SubWorkflowDto> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/sub-workflows`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || `Failed to create sub-workflow (HTTP ${response.status})`);
    }
    return fetchJson(response);
  },

  async updateSharedSubWorkflow(workflowId: string, data: UpdateSubWorkflowRequest): Promise<SubWorkflowDto> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/sub-workflows/${workflowId}`, {
      method: 'PUT', headers: jsonHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || `Failed to update sub-workflow (HTTP ${response.status})`);
    }
    return fetchJson(response);
  },

  async deleteSharedSubWorkflow(workflowId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/sub-workflows/${workflowId}`, {
      method: 'DELETE', headers: authHeader(),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || `Failed to delete sub-workflow (HTTP ${response.status})`);
    }
  },

  async getSharedSubWorkflowUsage(workflowId: string): Promise<SubWorkflowUsageResponse> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/sub-workflows/${workflowId}/usage`, { headers: authHeader() });
    if (!response.ok) throw new Error('Failed to fetch sub-workflow usage');
    return fetchJson(response);
  },

  async getSharedSubWorkflowHistory(workflowId: string): Promise<SubWorkflowHistoryEntry[]> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/sub-workflows/${workflowId}/history`, { headers: authHeader() });
    if (!response.ok) throw new Error('Failed to fetch sub-workflow history');
    return fetchJson(response);
  },

  async getWorkflowSecrets(): Promise<WorkflowSecret[]> {
    const response = await fetch(`${API_BASE_URL}/api/workflow-secrets`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch workflow secrets');
    return fetchJson(response);
  },

  async upsertWorkflowSecret(secretName: string, value: string): Promise<WorkflowSecret> {
    const response = await fetch(`${API_BASE_URL}/api/workflow-secrets/${encodeURIComponent(secretName)}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify({ value }),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const msg = errorBody?.error || errorBody?.message || `Failed to store workflow secret (HTTP ${response.status})`;
      throw new Error(msg);
    }
    return fetchJson(response);
  },

  async deleteWorkflowSecret(secretName: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/workflow-secrets/${encodeURIComponent(secretName)}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const msg = errorBody?.error || errorBody?.message || `Failed to delete workflow secret (HTTP ${response.status})`;
      throw new Error(msg);
    }
  },

  async createWorkflow(data: CreateWorkflowRequest): Promise<WorkflowDto> {
    const response = await fetch(`${API_BASE_URL}/api/workflows`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const msg = errorBody?.error || errorBody?.message || `Failed to create workflow (HTTP ${response.status})`;
      throw new Error(msg);
    }
    return fetchJson(response);
  },

  async updateWorkflow(workflowId: string, data: UpdateWorkflowRequest): Promise<WorkflowDto> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${workflowId}`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || errorBody?.message || `Failed to update workflow (HTTP ${response.status})`);
    }
    return fetchJson(response);
  },

  async deleteWorkflow(workflowId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${workflowId}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to delete workflow');
  },

  async toggleWorkflow(workflowId: string): Promise<{ enabled: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${workflowId}/toggle`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to toggle workflow');
    return fetchJson(response);
  },

  async getWorkflowSteps(workflowId: string): Promise<WorkflowStepDto[]> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${workflowId}/steps`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch workflow steps');
    return fetchJson(response);
  },

  async saveWorkflowSteps(workflowId: string, steps: WorkflowStepRequest[], expectedDefinitionRevision?: number): Promise<WorkflowStepDto[]> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${workflowId}/steps`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify({ steps, ...(expectedDefinitionRevision == null ? {} : { expectedDefinitionRevision }) }),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const msg = errorBody?.error || errorBody?.detail || errorBody?.message || `Failed to save workflow steps (HTTP ${response.status})`;
      throw new Error(msg);
    }
    const parsed = await fetchJson<WorkflowStepDto[]>(response);
    if (parsed && typeof parsed === 'object' && 'statusCode' in parsed && Number((parsed as any).statusCode) >= 400) {
      const body = (parsed as any).value || {};
      const msg = body.error || body.detail || body.message || `Failed to save workflow steps (HTTP ${(parsed as any).statusCode})`;
      throw new Error(msg);
    }
    return parsed;
  },

  async promoteWorkflowWebhookStep(
    workflowId: string,
    stepId: string,
    data: PromoteWorkflowWebhookStepRequest,
  ): Promise<PromoteWorkflowWebhookStepResponse> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${workflowId}/steps/${stepId}/promote-outbound`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const msg = errorBody?.error || errorBody?.detail || errorBody?.message || `Failed to promote webhook step (HTTP ${response.status})`;
      throw new Error(msg);
    }
    return fetchJson(response);
  },

  async rebindWorkflowIntegrationStep(
    workflowId: string,
    stepId: string,
    data: RebindWorkflowIntegrationStepRequest,
  ): Promise<RebindWorkflowIntegrationStepResponse> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${workflowId}/steps/${stepId}/rebind-outbound`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const msg = errorBody?.error || errorBody?.detail || errorBody?.message || `Failed to rebind integration action step (HTTP ${response.status})`;
      throw new Error(msg);
    }
    return fetchJson(response);
  },

  async getWorkflowInstances(workflowId: string): Promise<WorkflowInstanceDto[]> {
    const response = await fetch(`${API_BASE_URL}/api/workflows/${workflowId}/instances`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch workflow instances');
    return fetchJson(response);
  },

  async getWorkflowInstance(instanceId: string): Promise<{
    instance: WorkflowInstanceDto;
    steps: WorkflowStateEntryDto[];
    subWorkflowInstances?: WorkflowInstanceDto[];
  }> {
    const response = await fetch(`${API_BASE_URL}/api/workflow-instances/${instanceId}`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch workflow instance');
    return fetchJson(response);
  },

  async cancelWorkflowInstance(instanceId: string): Promise<{ status: string; instanceId: string }> {
    const response = await fetch(`${API_BASE_URL}/api/workflow-instances/${instanceId}/cancel`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    if (!response.ok) throw new Error('Failed to cancel workflow instance');
    return fetchJson(response);
  },

  async getAlertWorkflowProgress(alertId: string): Promise<AlertWorkflowProgress> {
    const response = await fetch(`${API_BASE_URL}/api/alerts/${alertId}/workflow-progress`, {
      headers: authHeader(),
    });
    if (!response.ok) throw new Error('Failed to fetch workflow progress');
    return fetchJson(response);
  },
};

export interface AlertWorkflowProgressEntry {
  stepId: string;
  stepOrder: number;
  stepType: string;
  stepName: string;
  status: string;
  message: string;
  startedAt?: string | null;
  completedAt?: string | null;
  resumesAt?: string | null;
  error?: string | null;
}

export interface AlertWorkflowProgressInstance {
  instanceId: string;
  parentInstanceId?: string | null;
  parentStepId?: string | null;
  workflowId: string;
  workflowName: string;
  triggerType: string;
  status: string;
  errorMessage?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  entries: AlertWorkflowProgressEntry[];
}

export interface AlertWorkflowProgress {
  alertId: string;
  instances: AlertWorkflowProgressInstance[];
}

// Re-export token helpers and types for convenience
export { getAuthToken, getRefreshToken, setTokens, clearTokens };
export type {
  User,
  UserDetail,
  InviteUserRequest,
  UpdateUserRequest,
  Group,
  Role,
  Integration,
  CreateIntegrationRequest,
  InboundIntegration,
  CreateInboundIntegrationRequest,
  UpdateInboundIntegrationRequest,
  OutboundIntegration,
  CreateOutboundIntegrationRequest,
  UpdateOutboundIntegrationRequest,
  ApiKeyInfo,
  RoutingRule,
  CreateRoutingRuleRequest,
  Schedule,
  ScheduleMember,
  ScheduleOverride,
  CreateScheduleRequest,
  CreateOverrideRequest,
  Rule,
  CreateRuleRequest,
  RuleAction,
  Condition,
  ConditionGroup,
  ActionConfig,
  NotificationChannel,
  CreateNotificationChannelRequest,
  UpdateNotificationChannelRequest,
  SmsGateway,
  CreateSmsGatewayRequest,
  UpdateSmsGatewayRequest,
  SmsDispatchMessage,
  CreateSmsDispatchMessageRequest,
  UserNotificationPreference,
  NotificationTarget,
};
// Workflow types are exported at their definition above
